/**
 * @GestionalePD User Registration Page UI
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Typography,
  OutlinedInput,
  InputAdornment,
  Stack,
  Divider,
  DialogContent,
  DialogActions,
  Dialog,
  Tooltip,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Alert,
  useMediaQuery,
  InputLabel,
  DialogTitle,
  Switch,
  Backdrop,
  CircularProgress,
  Link,
  Button,
} from '@mui/material';
import {
  ContentCopy,
  Visibility,
  VisibilityOff,
  Autorenew as AutorenewIcon,
  InfoOutlined as InfoOutlinedIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  WarningAmber as WarningAmberIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTheme } from '@mui/material/styles';
//-----------------------------------------------------------------------------------
import { LoadingUI } from '../utils/LayoutStyles';
import { POPUP_DURATION_SUCCESS, usePopup } from '../context/PopupContext';
import { CustomTextField, CustomAutocomplete, CustomDatePicker, CustomFormControl } from '../utils/StyledInputs';
import { CustomIconButton, DefaultContainedButton, DefaultOutlinedButton } from '../utils/Buttons';
import { validatePhoneNumber } from '../utils/numberValidity';
import { HttpErrorHandler } from '../utils/ErrorHandler';
import { PaypalCheckout } from './Checkout';
import genderList from '../model/gender';
import GlobalData from '../globalData';
import API from '../api';
import {
  getRegistrationConfig,
  GD_AGE_ERROR_MESSAGE,
  GD_ENTITY_REQUIRED_MESSAGE,
} from '../components/registration/registrationConfig';
import GdMasterEntitySelect from '../components/registration/GdMasterEntitySelect';
import { parseActiveRatesResponse, filterPaypalOnly } from '../utils/tesseramento/tesseramentoRates';
import { createFreeLinkedGdCard as requestFreeLinkedGdCard } from '../services/tesseramento/gdCardsService';
import { fetchGdEntities } from '../services/tesseramento/gdEntitiesService';
import { getActiveCampaign as fetchActiveCampaign } from '../services/tesseramento/campaignsService';
import PaymentMethodOption from '../components/tesseramento/PaymentMethodOption';
import RatesTable from '../components/tesseramento/RatesTable';
import BankTransferInfo from '../components/tesseramento/BankTransferInfo';

dayjs.locale('it');

/**
 * @returns Registration Page UI
 */
export default function RegistrationPage({ mode = 'PD' }) {
  // Configurazione per modalità (PD default | GD). Rami GD additivi (sprint 019).
  const registrationConfig = useMemo(() => getRegistrationConfig(mode), [mode]);
  const isGdMode = mode === 'GD';
  // General
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const is900Screen = useMediaQuery('(min-width: 900px)');
  const is1200Screen = useMediaQuery('(min-width: 1200px)');
  const navigate = useNavigate();
  const location = useLocation();
  // Chi arriva da un link con state.startAtStep1 (es. l'avviso GD → /join) salta la schermata
  // di scelta e parte direttamente dalla nuova iscrizione, come avviene già in modalità GD.
  const startAtStep1 = location.state?.startAtStep1 === true;
  // In modalità GD si parte già dallo step 1 (niente landing/step 0): evita il flash della
  // schermata di scelta, dato che l'utente ha già scelto i GD arrivando su /join-gd.
  const [registrationStep, setRegistrationStep] = useState(mode === 'GD' || startAtStep1 ? 1 : 0);
  const [isNewRegistration, setIsNewRegistration] = useState(mode === 'GD' || startAtStep1);
  const [isRenewRegistration, setIsRenewRegistration] = useState(false);
  const [isVolounteerRegistration, setIsVolunteerRegistration] = useState(false);
  const theme = useTheme();

  // Set if is a new registration
  const handleNewRegistration = (e) => {
    e.preventDefault();

    changeRegistrationStep();
    setIsNewRegistration(true);
  };

  // Set if is a renew registration
  // const handleRenewRegistration = (e) => {
  //   e.preventDefault();

  //   changeRegistrationStep();
  //   setIsRenewRegistration(true);
  // };

  const [errors, setErrors] = useState({
    name: false,
    surname: false,
    email: false,
    gender: false,
    name_alias: false,
    birth_date: false,
    place_of_birth: false,
    home_country_id: false,
    home_city_id: false,
    home_city_name: false,
    home_province_id: false,
    home_region_id: false,
    home_region_name: false,
    home_street: false,
    home_zip_code: false,
    call_code: false,
    res_street: false,
    phone: false,
    fiscal_code: false,
    res_country_id: false,
    res_city_id: false,
    res_city_name: false,
    res_province_id: false,
    res_region_id: false,
    res_region_name: false,
    entity_id: false,
    res_zip_code: false,
  });

  // Popup handler
  const { showPopup: contextShowPopup, setPopUpText } = usePopup();
  const showPopup = (success, changeStep, text) => {
    contextShowPopup(success, text);

    if (changeStep) {
      const totalDelay = success ? POPUP_DURATION_SUCCESS : 0;
      const animationDelay = 400;
      setTimeout(() => {
        changeRegistrationStep();
      }, totalDelay + animationDelay);
    }
  };

  const [step5Data, setStep5Data] = useState({
    text: 'Grazie per aver completato la registrazione',
    icon: 'check',
  });

  useEffect(() => {
    if (registrationStep !== 5) {
      setStep5Data({
        text: 'Grazie per aver completato la registrazione',
        icon: 'check',
      });
    }
  }, [registrationStep]);

  // Set Registration Step Page
  const changeRegistrationStep = (hasPayed = false, availableCampaign = true) => {
    if (registrationStep === 0) {
      setRegistrationStep(1);
    } else if (registrationStep === 1 && isNewRegistration) {
      setRegistrationStep(2);
    } else if (registrationStep === 1 && isRenewRegistration) {
      setRegistrationStep(4);
    } else if (registrationStep === 2) {
      setRegistrationStep(3);
    } else if (registrationStep === 3 && cardCheck && availableCampaign) {
      setRegistrationStep(4);
    } else if (registrationStep === 4 && cardCheck && availableCampaign && !hasPayed) {
      setRegistrationStep(4);
    } else if (registrationStep === 4 && cardCheck && hasPayed && paymentMethod.type === 'BB') {
      let unpayedStatusID;
      let bonificoPaymentTypeID;

      paymentStatus.forEach((staus) => {
        if (staus.type === 'W') {
          unpayedStatusID = staus.id;
        }
      });

      paymentTypes.forEach((type) => {
        if (type.type === 'BB') {
          bonificoPaymentTypeID = type.id;
        }
      });

      const data = {
        user_id: userInfo.userID,
        campaign_id: membershipCard.campaign_id,
        payment_status: unpayedStatusID,
        payment_type: bonificoPaymentTypeID,
        payment_entity_id: membershipCard.entity_id,
        payment_online: 1,
      };

      API.put(`/memcards/${membershipCard.id}`, data)
        .then((response) => {
          setRegistrationStep(5);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });
    } else {
      setRegistrationStep(5);
    }
  };

  // ----------------------- Section 1 -------------------------
  // user details form
  const [userFiscalCode, setUserFiscalCode] = useState('');
  const [useAlias, setUseAlias] = useState(false);
  const [alias, setAlias] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthPlace, setBirthPlace] = useState(null);
  const [birthPlacesList, setBirthPlacesList] = useState([]);
  // Età minima per registrarsi, dipendente dal flusso: PD ≥ 16, GD ≥ 14 (da registrationConfig).
  const minAgeCutoff = dayjs().subtract(registrationConfig.ageBounds.min, 'year');
  const [hasLessThan16AndMoreThan16YO, setHasLessThan16AndMoreThan16YO] = useState(false);
  const [isGdAvailable, setIsGdAvailable] = useState(false);
  // Eleggibilità GD 16–29 estremi inclusi (>=16 && <30) — distinta da isGdAvailable (sconto
  // giovani PD, age<30). Guida il gating del checkbox GD e il blocco del flusso GD-only.
  const [isGdEligible, setIsGdEligible] = useState(false);
  // Entità master GD (entity_master_gd): flusso GD-only e PD-bundle
  const [gdEntity, setGdEntity] = useState(null);
  const [gdEntityList, setGdEntityList] = useState([]);
  const [gdEntityListEmpty, setGdEntityListEmpty] = useState(false);
  const [campaignGd, setCampaignGd] = useState({});
  const [emailAddress, setEmailAddress] = useState('');
  const [actualEmailAddress, setActualEmailAddress] = useState('');
  const [newEmailAddress, setNewEmailAddress] = useState('');
  const [disableEmailAddress, setDisableEmailAddress] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [gender, setGender] = useState('');
  const [hasRole, setHasRole] = useState(false);
  const [role, setRole] = useState('');
  const [domRes, setDomRes] = useState(true);
  const [entity, setEntity] = useState('');
  const [isBlacklistedEntity, setIsBlacklistedEntity] = useState(false);
  const [disableEntity, setDisableEntity] = useState(false);
  const [entitiesFilterByResidence, setEntitiesFilterByResidence] = useState(true);
  const [callCode, setCallCode] = useState(null);
  const [italy, setItaly] = useState(null);
  const [italyApiCalled, setItalyApiCalled] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState({});
  const [isSubscriptionLocked, setIsSubscriptionLocked] = useState(false);

  // Residence
  const [resCountry, setResCountry] = useState(null);
  const [resRegion, setResRegion] = useState('');
  const [resProvince, setResProvince] = useState('');
  const [resCity, setResCity] = useState('');
  const [resRegionName, setResRegionName] = useState(null);
  const [resCityName, setResCityName] = useState(null);
  const [resStreet, setResStreet] = useState('');

  // Domicile
  const [domCountry, setDomCountry] = useState('');
  const [domRegion, setDomRegion] = useState('');
  const [domProvince, setDomProvince] = useState('');
  const [domCity, setDomCity] = useState('');
  const [domRegionName, setDomRegionName] = useState(null);
  const [domCityName, setDomCityName] = useState(null);
  const [domStreet, setDomStreet] = useState('');

  // Comunication checkboxes
  const [cardCheck, setCardCheck] = useState(true);
  const [gdCheck, setGdCheck] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [newsletterChecked, setNewsletterChecked] = useState(false);

  // Dynamic Containers animation in user section
  const [roleDynamicContainer, setRoleDynamicContainer] = useState(0);
  const [domDynamicContainer, setDomDynamicContainer] = useState(0);
  const [aliasDynamicContainer, setAliasDynamicContainer] = useState(0);

  // Api Call Static Data
  const [resRegionList, setResRegionList] = useState([]);
  const [resCountriesList, setResCountriesList] = useState([]);
  const [resProvincesList, setResProvincesList] = useState([]);
  const [resCitiesList, setResCitiesList] = useState([]);
  const [domRegionList, setDomRegionList] = useState([]);
  const [domCountriesList, setDomCountriesList] = useState([]);
  const [domProvincesList, setDomProvincesList] = useState([]);
  const [domCitiesList, setDomCitiesList] = useState([]);
  const [PositionsList, setPositionsList] = useState([]);
  const [EntitiesList, setEntitiesList] = useState([]);
  const [emptyEntitiesList, setEmptyEntitiesList] = useState(false);
  const [activeRates, setActiveRates] = useState([]);
  const [totalPrice, setTotalPrice] = useState([]);
  const [disabledStates, setDisabledStates] = useState(activeRates.map(() => false));

  useEffect(() => {
    if (isVolounteerRegistration) {
      setCardCheck(false);
    }
  });

  /* ---------------------------------- Login --------------------------------- */
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const handleOpenBackdrop = () => {
    setOpenBackdrop(true);
  };

  const handleCloseBackdrop = () => {
    setOpenBackdrop(false);
  };

  useEffect(() => {
    if (entity && typeof entity === 'object' && Object.keys(entity).length > 0 && entity.blacklisted) {
      setIsBlacklistedEntity(true);
      setCardCheck(false);
    } else {
      setIsBlacklistedEntity(false);
      setCardCheck(true);
    }
  }, [entity]);

  const [loginResponse, setLoginResponse] = useState({});
  const [registerLoginDone, setRegisterLoginDone] = useState(false);

  // Set CardCheck if User Log In
  useEffect(() => {
    if (registerLoginDone) {
      setCardCheck(true);
    }
  }, [registerLoginDone]);

  // Login Data
  const [loginUserID, setLoginUserID] = useState('');
  const [loginName, setLoginName] = useState('');
  const [loginSurname, setLoginSurname] = useState('');
  const [loginUseAlias, setLoginUseAlias] = useState(false);
  const [loginAlias, setLoginAlias] = useState('');
  const [loginBirthDate, setLoginBirthDate] = useState(undefined);
  const [loginBirthPlace, setLoginBirthPlace] = useState('');
  const [loginGender, setLoginGender] = useState(undefined);
  const [loginFiscalCode, setLoginFiscalCode] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginNews, setLoginNews] = useState('');
  const [loginCallCode, setLoginCallCode] = useState('');
  const [loginPhone, setLoginPhone] = useState('');
  const [loginResCountry, setLoginResCountry] = useState('');
  const [loginResRegion, setLoginResRegion] = useState('');
  const [loginResProvince, setLoginResProvince] = useState('');
  const [loginResCity, setLoginResCity] = useState('');
  const [loginResStreet, setLoginResStreet] = useState('');
  const [loginResZipCode, setLoginResZipCode] = useState('');
  const [loginResEqualHome, setLoginResEqualHome] = useState(false);
  const [loginDomCountry, setLoginDomCountry] = useState('');
  const [loginDomRegion, setLoginDomRegion] = useState('');
  const [loginDomProvince, setLoginDomProvince] = useState('');
  const [loginDomCity, setLoginDomCity] = useState('');
  const [loginDomStreet, setLoginDomStreet] = useState('');
  const [loginDomZipCode, setLoginDomZipCode] = useState('');
  const [loginEntity, setLoginEntity] = useState({});
  const [loginCardHolder, setLoginCardHolder] = useState(null);
  const [loginHasRole, setLoginHasRole] = useState(false);
  const [loginPosition, setLoginPosition] = useState({});

  // Set Login Data
  useEffect(() => {
    if (registerLoginDone) {
      setLoginUserID(loginResponse.id);
      setLoginName(loginResponse.name);
      setLoginSurname(loginResponse.surname);
      setLoginAlias(loginResponse.name_alias);
      setLoginUseAlias(loginResponse.name_alias !== null);
      setLoginBirthDate(dayjs(loginResponse.birth_date));
      setLoginBirthPlace(loginResponse.place_of_birth);
      getLoginGender(loginResponse.gender);
      setLoginFiscalCode(loginResponse.fiscal_code);
      setLoginEmail(loginResponse.email);
      setLoginNews(loginResponse.allow_news);
      getLoginCallCode(loginResponse.call_code);
      setLoginPhone(loginResponse.phone);
      setLoginResCountry(loginResponse.res_country);
      setLoginResRegion(loginResponse.res_region);
      setLoginResProvince(loginResponse.res_province);
      setLoginResCity(loginResponse.res_city);
      setLoginResStreet(loginResponse.res_street);
      setLoginResZipCode(loginResponse.res_zip_code);
      setLoginResEqualHome(loginResponse.res_equal_home);
      setLoginDomCountry(loginResponse.home_country);
      setLoginDomRegion(loginResponse.home_region);
      setLoginDomProvince(loginResponse.home_province);
      setLoginDomCity(loginResponse.home_city);
      setLoginDomStreet(loginResponse.home_street);
      setLoginDomZipCode(loginResponse.home_zip_code);
      getLoginEntity(loginResponse.entities, loginResponse.entity_id);

      if (loginResponse.positions.length > 0) {
        setLoginHasRole(true);
        setRoleDynamicContainer(1);

        const highestPosition = loginResponse.positions.reduce((prev, current) => {
          return prev.level > current.level ? prev : current;
        });

        setLoginPosition(highestPosition);
      }
    }
  }, [loginResponse]);

  // Set DomRes like LoginDomRes
  useEffect(() => {
    if (registerLoginDone) {
      if (loginResEqualHome === 0) {
        setDomRes(false);
      }
    }
  }, [loginResEqualHome]);

  // Set UseAlias like LoginUseAlias
  useEffect(() => {
    if (registerLoginDone) {
      if (loginUseAlias) {
        setUseAlias(true);
      }
    }
  });

  // Get Login Gender
  const getLoginGender = (gender) => {
    let genderName;

    if (gender === 'male') {
      genderName = 'Uomo';
    } else if (gender === 'female') {
      genderName = 'Donna';
    } else if (gender === 'other') {
      genderName = 'Altro';
    } else {
      genderName = null;
    }

    return setLoginGender(genderName);
  };

  // Get Login CallCode
  const getLoginCallCode = (callCode) => {
    const foundedCallCode = resCountriesList.find((element) => element.call_code === callCode);

    return setLoginCallCode(foundedCallCode);
  };

  // Get Login User Master Entity
  const getLoginEntity = (userEntitiesList, userEntityID) => {
    const masterEntity = userEntitiesList.find((entity) => entity.id === userEntityID);

    setLoginEntity(masterEntity);
  };

  // Newsletter Checked
  const handleNewsletterCheck = () => setNewsletterChecked((current) => !current);

  // Role Dynamic Container Show/Hide
  useEffect(() => {
    if (hasRole) {
      setRoleDynamicContainer(1);
    } else {
      setRoleDynamicContainer(0);
    }
  }, [hasRole]);

  // Alias Dynamic Container Show/Hide
  useEffect(() => {
    if (useAlias) {
      setAliasDynamicContainer(1);
    } else {
      setAliasDynamicContainer(0);
    }
  }, [useAlias]);

  // Dom Dynamic Container Show/Hide
  useEffect(() => {
    if (domRes) {
      setDomDynamicContainer(0);
      setEntitiesFilterByResidence(true);
    } else {
      setDomDynamicContainer(1);
    }
  }, [domRes]);

  // Domicile = Residence checkbox
  const HandleDomRes = () => {
    setDomCountry('');
    setDomRegion('');
    setDomProvince('');
    setDomCity('');
    setDomStreet('');
    setDomRes((current) => !current);

    if (registerLoginDone) {
      setLoginResEqualHome(!loginResEqualHome);
    }
  };

  // Select Birth Date
  const handleBirthdateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY/MM/DD');
    setBirthDate(formattedDate);
  };

  // Set if Is GD Available
  // Nessuna pre-selezione automatica della spunta GD: la scelta resta all'utente (o è già
  // impostata dalla card "Iscriviti al Pd e ai GD"). Se però la data di nascita rende l'utente
  // NON eleggibile, azzeriamo la spunta perché il checkbox viene disabilitato.
  useEffect(() => {
    const birth = dayjs(birthDate);
    const today = dayjs();
    const age = today.diff(birth, 'year');
    const eligible = age >= 14 && age < 30; // eleggibilità GD 14–29 estremi inclusi

    setIsGdAvailable(age < 30); // sconto giovani PD (invariato)
    setIsGdEligible(eligible);
    setHasLessThan16AndMoreThan16YO(age >= 14 && age < 16); // 14–15: solo GD, non PD

    if (!eligible) {
      setGdCheck(false);
    }
  }, [birthDate]);

  // Membership Card Checkbox
  const handleCardCheck = () => {
    setCardCheck((current) => !current);
  };

  // Membership GD Checkbox
  const handleGdCheck = () => {
    setGdCheck((current) => !current);
  };

  // Modify info if user deselect cardCheck
  useEffect(() => {
    if (!cardCheck) {
      setHasRole(false);
      setRole('');
      setGdCheck(false);
    }
  }, [cardCheck]);

  useEffect(() => {
    setRole('');
  }, [hasRole]);

  useEffect(() => {
    if (birthPlacesList.length === 0) {
      API.get('/citiesfiscalcode').then((response) => {
        setBirthPlacesList(response.data);
      });
    }
  }, [birthPlacesList]);

  useEffect(() => {
    if (activeCampaign && Object.keys(activeCampaign).length === 0) {
      fetchActiveCampaign(registrationConfig.campaignType)
        .then((data) => {
          setActiveCampaign(data);
        })
        .catch((error) => {
          setIsSubscriptionLocked(true);
          setCardCheck(false);
        });
    }
  }, [activeCampaign]);

  // Entità master GD (GET /entities/gd_registration): in registrazione le entità GD vanno
  // filtrate per residenza/domicilio, esattamente come l'entità di tesseramento PD (niente
  // scelta libera su tutte). Stessa sorgente località dell'entità PD: login vs nuova
  // registrazione, residenza vs domicilio; estero ⇒ nessuna entità GD (branch GD nazionale).
  useEffect(() => {
    if (!isGdMode && !gdCheck) return;

    const useRes = entitiesFilterByResidence;
    const city = registerLoginDone ? (useRes ? loginResCity : loginDomCity) : useRes ? resCity : domCity;
    const province = registerLoginDone
      ? useRes
        ? loginResProvince
        : loginDomProvince
      : useRes
      ? resProvince
      : domProvince;
    const country = registerLoginDone ? null : useRes ? resCountry : domCountry;

    // Cambio località ⇒ azzera la selezione GD corrente (potrebbe non essere più valida).
    setGdEntity(null);

    // Estero (paese diverso dall'Italia): nessuna entità GD disponibile.
    if (country && country.id && italy?.id && country.id !== italy.id) {
      setGdEntityList([]);
      setGdEntityListEmpty(true);
      return;
    }

    if (city && city.id && province && province.id) {
      fetchGdEntities({ cityId: city.id, provinceId: province.id })
        .then(({ entities, empty }) => {
          setGdEntityList(entities);
          setGdEntityListEmpty(empty);
        })
        .catch(() => {
          setGdEntityList([]);
          setGdEntityListEmpty(true);
        });
    } else {
      // Località non ancora completa: nessuna lista (in attesa di città/provincia).
      setGdEntityList([]);
      setGdEntityListEmpty(false);
    }
  }, [
    isGdMode,
    gdCheck,
    entitiesFilterByResidence,
    registerLoginDone,
    resCity,
    resProvince,
    resCountry,
    domCity,
    domProvince,
    domCountry,
    loginResCity,
    loginResProvince,
    loginDomCity,
    loginDomProvince,
    italy?.id,
  ]);

  // Handle Entity Field
  useEffect(() => {
    setDisableEntity(true);
    if (
      entitiesFilterByResidence &&
      resCity !== null &&
      resCity !== undefined &&
      resCity !== '' &&
      resProvince !== null &&
      resProvince !== undefined &&
      resProvince !== ''
    ) {
      API.get(`/entities/registration?city_id=${resCity.id}&province_id=${resProvince.id}`)
        .then((response) => {
          setEntitiesList(response.data.entities);
          setEmptyEntitiesList(response.data.empty);
          setDisableEntity(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });
    } else if (
      entitiesFilterByResidence &&
      resCountry !== null &&
      resCountry !== undefined &&
      resCountry !== '' &&
      resCountry.id !== italy?.id
    ) {
      API.get(`/entities/abroad_registration?id=${resCountry.id}`)
        .then((response) => {
          setEntitiesList(response.data.entities);
          setEmptyEntitiesList(response.data.empty);
          setDisableEntity(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });
    } else if (
      !entitiesFilterByResidence &&
      domCity !== null &&
      domCity !== undefined &&
      domCity !== '' &&
      domProvince !== null &&
      domProvince !== undefined &&
      domProvince !== ''
    ) {
      API.get(`/entities/registration?city_id=${domCity.id}&province_id=${domProvince.id}`)
        .then((response) => {
          setEntitiesList(response.data.entities);
          setEmptyEntitiesList(response.data.empty);
          setDisableEntity(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });
    } else if (
      !entitiesFilterByResidence &&
      domCountry !== null &&
      domCountry !== undefined &&
      domCountry !== '' &&
      domCountry.id !== italy?.id
    ) {
      API.get(`/entities/abroad_registration?id=${domCountry.id}`)
        .then((response) => {
          setEntitiesList(response.data.entities);
          setEmptyEntitiesList(response.data.empty);
          setDisableEntity(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });
    }
  }, [resCity, resProvince, domCity, domProvince, resCountry, domCountry, entitiesFilterByResidence]);

  const [resetEntity, setResetEntity] = useState(false);

  // Handle Login Entity Field
  useEffect(() => {
    if (registerLoginDone) {
      setDisableEntity(true);

      if (resetEntity) {
        setLoginEntity(null);
      }

      if (
        entitiesFilterByResidence &&
        loginResCity !== null &&
        loginResCity !== undefined &&
        loginResCity !== '' &&
        loginResProvince !== null &&
        loginResProvince !== undefined &&
        loginResProvince !== ''
      ) {
        API.get(`/entities/registration?city_id=${loginResCity.id}&province_id=${loginResProvince.id}`)
          .then((response) => {
            setEntitiesList(response.data.entities);
            setEmptyEntitiesList(response.data.empty);
            setDisableEntity(false);
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            showPopup(false, false);
          });
      } else if (
        !entitiesFilterByResidence &&
        loginDomCity !== null &&
        loginDomCity !== undefined &&
        loginDomCity !== '' &&
        loginDomProvince !== null &&
        loginDomProvince !== undefined &&
        loginDomProvince !== ''
      ) {
        API.get(`/entities/registration?city_id=${loginDomCity.id}&province_id=${loginDomProvince.id}`)
          .then((response) => {
            setEntitiesList(response.data.entities);
            setEmptyEntitiesList(response.data.empty);
            setDisableEntity(false);
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            showPopup(false, false);
          });
      }

      if (!resetEntity) {
        setResetEntity(true);
      }
    }
  }, [loginResCity, loginResProvince, loginDomCity, loginDomProvince, entitiesFilterByResidence]);

  // Empty Login Entity Field
  useEffect(() => {
    if (registerLoginDone) {
      setLoginEntity(null);
    } else {
      setEntity(null);
    }
  }, [entitiesFilterByResidence]);

  useEffect(() => {
    if (registerLoginDone) {
      if (disableEntity) {
        setLoginEntity(null);
      }
    }

    if (!registerLoginDone) {
      if (disableEntity) {
        setEntity(null);
      }
    }
  }, [disableEntity]);

  // Get api calls for countries and positions
  useEffect(() => {
    if (resCountriesList.length === 0 && domCountriesList.length === 0 && PositionsList.length === 0) {
      axios
        .all([API.get('/countries'), API.get('/institutional_positions')])
        .then(
          axios.spread((countriesResponse, positionsResponse) => {
            setResCountriesList(countriesResponse.data);
            setDomCountriesList(countriesResponse.data);
            setPositionsList(positionsResponse.data.institutional_positions);
          })
        )
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });
    }
  }, [resCountriesList, domCountriesList, PositionsList]);

  // Get Region List
  const getRegionList = (country, setRegionList, setProvincesList, setCitiesList) => {
    if (country && typeof country === 'object' && Object.keys(country).length > 0) {
      API.get(`/countries/regions`, {
        params: {
          id: country.id,
        },
      }).then((response) => {
        if (response.data.length > 0) {
          setRegionList(response.data);
        } else {
          setRegionList([]);
          setProvincesList([]);
          setCitiesList([]);
        }
      });
    }
  };

  // Get Res Region List
  useEffect(() => {
    getRegionList(resCountry, setResRegionList, setResProvincesList, setResCitiesList);
  }, [resCountry]);

  // Get Dom Region List
  useEffect(() => {
    getRegionList(domCountry, setDomRegionList, setDomProvincesList, setDomCitiesList);
  }, [domCountry]);

  // Get Login Res Region List
  useEffect(() => {
    getRegionList(loginResCountry, setResRegionList, setResProvincesList, setResCitiesList);
  }, [loginResCountry]);

  // Get Login Dom Region List
  useEffect(() => {
    getRegionList(loginDomCountry, setDomRegionList, setDomProvincesList, setDomCitiesList);
  }, [loginDomCountry]);

  // Get Province List
  const getProvinceList = (region, setProvincesList, setCitiesList) => {
    if (region && typeof region === 'object' && Object.keys(region).length > 0) {
      API.get(`/regions/provinces`, {
        params: {
          region_name: region.region_name,
        },
      })
        .then((response) => {
          if (response.data.length > 0) {
            setProvincesList(response.data);
          } else {
            setProvincesList([]);
            setCitiesList([]);
          }
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });
    }
  };

  // Get Res Province List
  useEffect(() => {
    getProvinceList(resRegion, setResProvincesList, setResCitiesList);
  }, [resRegion]);

  // Get Dom Province List
  useEffect(() => {
    getProvinceList(domRegion, setDomProvincesList, setDomCitiesList);
  }, [domRegion]);

  // Get Login Res Province List
  useEffect(() => {
    getProvinceList(loginResRegion, setResProvincesList, setResCitiesList);
  }, [loginResRegion]);

  // Get Login Dom Province List
  useEffect(() => {
    getProvinceList(loginDomRegion, setDomProvincesList, setDomCitiesList);
  }, [loginDomRegion]);

  // Get City List
  const getCityList = (province, setCitiesList) => {
    if (province && typeof province === 'object' && Object.keys(province).length > 0) {
      API.get(`/provinces/cities`, {
        params: {
          id: province.id,
        },
      })
        .then((response) => {
          if (response.data.length > 0) {
            setCitiesList(response.data);
          } else {
            setCitiesList([]);
          }
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });
    }
  };

  // Get Res City List
  useEffect(() => {
    getCityList(resProvince, setResCitiesList);
  }, [resProvince]);

  // Get Dom City List
  useEffect(() => {
    getCityList(domProvince, setDomCitiesList);
  }, [domProvince]);

  // Get Login Res City List
  useEffect(() => {
    getCityList(loginResProvince, setResCitiesList);
  }, [loginResProvince]);

  // Get Login Dom City List
  useEffect(() => {
    getCityList(loginDomProvince, setDomCitiesList);
  }, [loginDomProvince]);

  // Get Default Country and CallCode
  useEffect(() => {
    const italyData = resCountriesList.find((element) => element.name === 'Italia');
    setItaly(italyData);
    setCallCode(italyData);
    setResCountry(italyData);
  }, [resCountriesList]);

  useEffect(() => {
    if (italy !== null && italy !== undefined && typeof italy === 'object') {
      setItalyApiCalled(true);
    }
  }, [italy]);

  // Api response data
  const [userInfo, setUserInfo] = useState({
    userID: '',
    userPosition: '',
    userEntity: '',
  });

  // Get selected gender
  const getGenderName = (name) => {
    let gender;

    if (name === 'uomo') {
      gender = 'male';
    } else if (name === 'donna') {
      gender = 'female';
    } else if (name === 'altro') {
      gender = 'other';
    } else {
      gender = null;
    }

    return gender;
  };

  // User registration request
  const onRegisterUser = (event) => {
    event.preventDefault();
    const _userInfo = new FormData(event.currentTarget);
    const genderName = getGenderName(gender ? gender.gender.toLowerCase() : '');

    handleOpenBackdrop();

    if (registerLoginDone) {
      if (loginCardHolder === 0) {
        const newData = {
          name: loginName,
          surname: loginSurname,
          fiscal_code: loginFiscalCode,
          email: loginEmail,
          birth_date: dayjs(loginBirthDate).format('YYYY/MM/DD'),
          place_of_birth: birthPlace,
          res_equal_home: Number(domRes),
          home_city_id: domRes ? loginResCity.id : loginDomCity.id,
          home_province_id: domRes ? loginResProvince.id : loginDomProvince.id,
          home_country_id: domRes ? loginResCountry.id : loginDomCountry.id,
          home_street: domRes ? loginResStreet : loginDomStreet,
          home_zip_code: loginDomZipCode,
          phone: loginPhone,
          call_code: loginCallCode.call_code,
          res_city_id: loginResCity.id,
          res_province_id: loginResProvince.id,
          res_country_id: loginResCountry.id,
          res_street: loginResStreet,
          res_zip_code: loginResZipCode,
          allow_news: loginNews,
          entity_id: loginEntity.id,
        };

        if (role) newData.position = role.id;
        if (useAlias) {
          newData.gender = loginGender;
          newData.name_alias = loginAlias;
        }

        let itemsLeft = false;
        Object.keys(newData).forEach((key) => {
          const value = newData[key];

          if (Object.prototype.hasOwnProperty.call(newData, key)) {
            if (
              (value === undefined ||
                value === null ||
                value === 'Invalid Date' ||
                (typeof value === 'string' && value.length === 0)) &&
              !(
                (key === 'home_country_id' && newData.res_equal_home) ||
                (key === 'home_region_id' && newData.res_equal_home) ||
                (key === 'home_province_id' && newData.res_equal_home) ||
                (key === 'home_city_id' && newData.res_equal_home) ||
                (key === 'home_street' && newData.res_equal_home) ||
                (key === 'home_zip_code' && newData.res_equal_home) ||
                key === 'gender'
              )
            ) {
              itemsLeft = true;
              contextShowPopup('warning', 'Inserisci i dati mancanti');
            }
          }
        });

        if (!itemsLeft) {
          delete newData.home_region_id;
          delete newData.res_region_id;

          API.put(`/users/${loginUserID}`, newData)
            .then((response) => {
              getPaymentStatus();
              getPaymentTypes();
              getBankData();
              getActiveRates(role.id, loginEntity.id, true);
              handleCloseBackdrop();
            })
            .catch((error) => {
              handleCloseBackdrop();
              HttpErrorHandler(error, setPopUpText);
              showPopup(false, false);
            });
        }
      } else {
        handleCloseBackdrop();
        setPopUpText('Utente già tesserato');
        showPopup(false, false);
      }
    } else {
      const oData = {
        name: _userInfo.get('name'),
        surname: _userInfo.get('surname'),
        email: emailAddress.replace(/\s+/g, ''),
        birth_date: birthDate,
        place_of_birth: birthPlace ? birthPlace.id : '',
        res_equal_home: Number(domRes),
        home_country_id: domRes ? (resCountry ? resCountry.id : '') : domCountry ? domCountry.id : '',
        home_street: domRes ? resStreet : domStreet,
        home_zip_code: domRes ? _userInfo.get('res-cap') : _userInfo.get('dom-cap'),
        call_code: _userInfo.get('call_code'),
        allow_news: newsletterChecked ? 1 : 0,
        res_street: resStreet,
        phone: phoneNumber,
        fiscal_code: _userInfo.get('fiscal-code').toUpperCase(),
        res_country_id: resCountry ? resCountry.id : '',
        entity_id: entity ? entity.id : '',
        res_zip_code: _userInfo.get('res-cap'),
        gd_check: Number(gdCheck),
      };

      if (resCountry !== null && resCountry !== undefined && resCountry !== '' && resCountry.id === italy?.id) {
        oData.res_city_id = resCity ? resCity.id : '';
        oData.res_province_id = resProvince ? resProvince.id : '';
        oData.res_region_id = resRegion ? resRegion.region_name : '';

        if (domRes) {
          oData.home_city_id = resCity ? resCity.id : '';
          oData.home_province_id = resProvince ? resProvince.id : '';
          oData.home_region_id = resRegion ? resRegion.region_name : '';
        }
      } else {
        oData.res_city_name = resCityName || '';
        oData.res_region_name = resRegionName || '';

        if (domRes) {
          oData.home_city_name = resCityName || '';
          oData.home_region_name = resRegionName || '';
        }
      }

      if (!domRes) {
        if (domCountry !== null && domCountry !== undefined && domCountry !== '' && domCountry.id === italy?.id) {
          oData.home_city_id = domCity ? domCity.id : '';
          oData.home_province_id = domProvince ? domProvince.id : '';
          oData.home_region_id = domRegion ? domRegion.region_name : '';
        } else {
          oData.home_city_name = domCityName || '';
          oData.home_region_name = domRegionName || '';
        }
      }

      if (role) oData.position = role.id;
      if (useAlias) {
        oData.gender = genderName;
        oData.name_alias = alias;
      }

      // Sprint 019 GD — discriminante campagna + entità master GD e validazioni dedicate.
      const subscribesGd = isGdMode || gdCheck;
      if (registrationConfig.campaignType && registrationConfig.campaignType !== 'PD') {
        oData.campaign_type = registrationConfig.campaignType;
      }
      if (subscribesGd) {
        oData.entity_master_gd = gdEntity ? gdEntity.id : '';

        // Blocco età 16–29 estremi inclusi (data di iscrizione) e entità master GD obbligatoria.
        if (!isGdEligible) {
          handleCloseBackdrop();
          setErrors((prev) => ({ ...prev, birth_date: true }));
          contextShowPopup('warning', GD_AGE_ERROR_MESSAGE);
          return;
        }
        if (!gdEntity) {
          handleCloseBackdrop();
          setErrors((prev) => ({ ...prev, entity_master_gd: true }));
          contextShowPopup('warning', GD_ENTITY_REQUIRED_MESSAGE);
          return;
        }
      }

      let itemsLeft = false;
      const newErrors = { ...errors };

      Object.keys(oData).forEach((key) => {
        const value = oData[key];

        if (Object.prototype.hasOwnProperty.call(oData, key)) {
          if (
            (value === undefined ||
              value === null ||
              value === 'Invalid Date' ||
              (typeof value === 'string' && value.length === 0)) &&
            !(
              (key === 'home_country_id' && oData.res_equal_home) ||
              (key === 'home_region_id' && oData.res_equal_home) ||
              (key === 'home_province_id' && oData.res_equal_home) ||
              (key === 'home_city_id' && oData.res_equal_home) ||
              (key === 'home_street' && oData.res_equal_home) ||
              (key === 'home_zip_code' && oData.res_equal_home) ||
              key === 'gender'
            )
          ) {
            itemsLeft = true;
            if (Object.prototype.hasOwnProperty.call(newErrors, key)) {
              newErrors[key] = true;
            }
            handleCloseBackdrop();
            contextShowPopup('warning', 'Inserisci i dati mancanti');
          } else {
            newErrors[key] = false;
          }
        }
      });

      setErrors(newErrors);

      if (!itemsLeft) {
        if (resCountry !== null && resCountry !== undefined && resCountry !== '' && resCountry.id === italy?.id) {
          delete oData.home_region_id;
        } else {
          delete oData.home_region_name;
        }

        if (domCountry !== null && domCountry !== undefined && domCountry !== '' && domCountry.id === italy?.id) {
          delete oData.res_region_id;
        } else {
          delete oData.res_region_name;
        }

        if (dayjs(birthDate).isBefore(dayjs(minAgeCutoff))) {
          API.post('/users', oData)
            .then((response) => {
              handleCloseBackdrop();

              if (response.status === 200) {
                if (response.data !== '') {
                  setUserInfo((prevState) => ({
                    ...prevState,
                    userID: response.data.user.id,
                    userPosition: role.id,
                    userEntity: response.data.user.entity_id,
                  }));
                  setActualEmailAddress(response.data.user.email);
                  setNewEmailAddress(response.data.user.email);
                  setPopUpText('Registrazione avvenuta con successo');
                  showPopup(true, true);
                }
              } else if (response.status === 202) {
                setStep5Data({
                  text: response.data.message,
                  icon: 'check',
                });
                setRegistrationStep(5);
              }
            })
            .catch((error) => {
              handleCloseBackdrop();
              if (error.response.status === 409) {
                setStep5Data({
                  text: error.response.data.message,
                  icon: 'warning',
                });
                setRegistrationStep(5);
              } else {
                setPopUpText(error.response.data.message);
                showPopup(false, false);
              }
            });
        } else {
          handleCloseBackdrop();
          setPopUpText(`Per iscriverti devi avere almeno ${registrationConfig.ageBounds.min} anni`);
          showPopup(false, false);
        }
      }
    }
  };

  /* -------------------------------- Section 2 ------------------------------- */
  const [showNewPassword, setShowNewPassword] = useState({});
  const [showRepeatPassword, setShowRepeatPassword] = useState({});
  const [isValidatedPassword, setIsValidatedPassword] = useState(false);

  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const handleClickShowRepeatPassword = () => setShowRepeatPassword((show) => !show);

  // Set User Password
  const handleSetUserPassword = async (e) => {
    try {
      e.preventDefault();

      const data = new FormData(e.currentTarget);
      const newPassword = data.get('newPassword');
      const repeatPassword = data.get('repeatPassword');

      if (newPassword !== repeatPassword) {
        throw new Error('Le password inserite devono essere uguali');
      }

      const isMaiuscValid = /^(?=.*[A-Z])/.test(newPassword);
      const isNumberValid = /(?=.*\d)/.test(newPassword);
      const isSpecialValid = /(?=.*[!?@#$%^&*()_+-])/.test(newPassword);

      if (!isMaiuscValid) {
        throw new Error('La password deve contenere almeno una lettera maiuscola');
      }

      if (!isNumberValid) {
        throw new Error('La password deve contenere almeno un numero');
      }

      if (!isSpecialValid) {
        throw new Error('La password deve contenere almeno un carattere speciale');
      }

      if (newPassword.length < 8) {
        throw new Error('La password deve contenere almeno 8 caratteri');
      }

      const userPasswordData = {
        password: newPassword,
      };

      await API.patch(`/users/${userInfo.userID}/setPwd`, userPasswordData).then((response) => {
        getPaymentTypes();
        loginUser(newPassword);
        getPaymentStatus();
        getBankData();
        if (cardCheck) {
          getActiveRates(role.id, response.data.user.entity_id, true);
        }
        setPopUpText('Password impostata correttamente');
        showPopup(true, false);
        if (!cardCheck) {
          setTimeout(() => {
            navigate('/');
          }, POPUP_DURATION_SUCCESS);
        }
      });
    } catch (error) {
      const errorMessage = error.message;
      setPopUpText(errorMessage);
      showPopup(false, false);
    }
  };

  const loginUser = (password) => {
    const loginData = {
      username: userFiscalCode,
      password,
    };

    API.post('/login', loginData).then((response) => {
      if (response.status === 200) {
        const _responseUserData = response.data.user;
        const _responseUserAuthorization = response.data.authorisation;

        GlobalData.loginData = loginData;
        GlobalData.userData = _responseUserData;
        GlobalData.token = _responseUserAuthorization.token;
      }
    });
  };

  // set local storage data
  useEffect(() => {
    if (
      GlobalData.loginData !== undefined &&
      GlobalData.userData !== undefined &&
      GlobalData.token !== undefined &&
      GlobalData.loginData !== null &&
      GlobalData.userData !== null &&
      GlobalData.token !== null
    ) {
      localStorage.setItem('loginData', JSON.stringify(GlobalData.loginData));
      localStorage.setItem('userData', JSON.stringify(GlobalData.userData));
      localStorage.setItem('token', GlobalData.token);
    }
  }, [GlobalData.loginData, GlobalData.userData, GlobalData.token]);
  // ---------------------------- Section 3 ------------------------------
  // comunication media verification section
  const [verifiedEmail, setVerifiedEmail] = useState(false);
  const [isUserVerifyingEmail, setIsUserVerifyingEmail] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailCountdown, setEmailCountdown] = useState(0);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState(false);
  const [isUserVerifyingPhone, setIsUserVerifyingPhone] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [isSmsSent, setIsSmsSent] = useState(false);

  const handleSubmitChangeEmail = () => {
    if (!disableEmailAddress) {
      API.patch(`/users/${userInfo.userID}/change_email`, { email: newEmailAddress })
        .then((response) => {
          if (response.status === 200) {
            setEmailAddress(newEmailAddress);
          }
          setActualEmailAddress(newEmailAddress);
        })
        .catch((error) => {});
    }

    setDisableEmailAddress(!disableEmailAddress);
  };

  // Verify Email OTP check request
  const onVerifyEmailOTP = () => {
    const otpData = {
      otp: emailVerificationCode,
      email: actualEmailAddress,
    };

    API.post(`/checkemailotp`, otpData)
      .then((response) => {
        if (response.status === 202) {
          setPopUpText("L'indirizzo email è stato verificato");
          showPopup(true, false);
          setVerifiedEmail(true);
          setIsUserVerifyingEmail(false);
        }
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false, false);
      });
  };

  // Resend Email Verification Code
  const resendVerificationEmail = () => {
    if (emailCountdown > 0) return;

    API.patch('/otp/send_mail', { email: actualEmailAddress, user_id: userInfo.userID })
      .then((response) => {
        setPopUpText('Email inviata con successo');
        showPopup(true, false);

        setIsEmailSent(true);
        setEmailCountdown(30);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false, false);
      });
  };

  useEffect(() => {
    let timer;
    if (emailCountdown > 0) {
      timer = setInterval(() => {
        setEmailCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
    } else if (emailCountdown === 0 && isSmsSent) {
      setIsEmailSent(false);
    }
    return () => clearInterval(timer);
  }, [emailCountdown, isEmailSent]);

  const onVerifySmsOTP = () => {
    API.post(`/checksmsotp?otp=${phoneVerificationCode}&phone=${callCode.call_code}${phoneNumber}`)
      .then((response) => {
        if (response.status === 202) {
          setPopUpText('Il numero è stato verificato');
          showPopup(true, false);
          setVerifiedPhone(true);
          setIsUserVerifyingPhone(false);
        }
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false, false);
      });
  };

  const resendVerificationSms = () => {
    if (smsCountdown > 0) return;

    const countryCode = callCode.call_code.slice(1, callCode.call_code.length);

    API.patch(`/otp/send_sms?phone=${phoneNumber}&country_code=${countryCode}`)
      .then((response) => {
        setPopUpText('SMS inviato con successo');
        showPopup(true, false);

        setIsSmsSent(true);
        setSmsCountdown(30);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false, false);
      });
  };

  useEffect(() => {
    let timer;
    if (smsCountdown > 0) {
      timer = setInterval(() => {
        setSmsCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
    } else if (smsCountdown === 0 && isSmsSent) {
      setIsSmsSent(false);
    }
    return () => clearInterval(timer);
  }, [smsCountdown, isSmsSent]);

  // Request for proceeding to checkout step
  const onCompletedVerification = () => {
    if (verifiedEmail && verifiedPhone) {
      changeRegistrationStep();
    } else {
      setPopUpText('Per proseguire è necessario verificare entrambi i contatti');
      showPopup(false, false);
    }
  };

  // ---------------------------- Section 4 ------------------------------
  // Paypal checkout handler
  const [createdCard, setCreatedCard] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState({
    select: false,
    method: null,
    type: null,
  });
  const [onlineMembershipCard, setOnlineMembershipCard] = useState(true);

  const [membershipCard, setMembershipCard] = useState({
    id: null,
    card_number: null,
    prefix: null,
    campaign_id: null,
    campaign_name: null,
    entity_name: null,
    entity_id: null,
    amount: null,
    currency: null,
    creation_date: null,
    end_date: null,
    payment_status: null,
    payment_type: null,
  });

  const [openCreateCardDialog, setOpenCreateCardDialog] = useState(false);

  const handleOpenCreateCardDialog = () => {
    setOpenCreateCardDialog(true);
  };

  const handleCloseCreateCardDialog = () => {
    setOpenCreateCardDialog(false);
  };

  // get payment status data
  const getPaymentStatus = () => {
    API.get('/paystatus')
      .then((response) => {
        setPaymentStatus(response.data);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false, false);
      });
  };

  // get payment type data
  const getPaymentTypes = () => {
    API.get('/paytypes?for_user=1')
      .then((response) => {
        setPaymentTypes(response.data.payment_types || []);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false, false);
      });
  };

  // Get Active Rates
  const getActiveRates = (position, entity, changeStep) => {
    // GD: quota sull'entità master GD e discriminante campaign_type=GD (tariffa singola).
    const rateEntity = isGdMode && gdEntity ? gdEntity.id : entity;
    const typeParam =
      registrationConfig.campaignType && registrationConfig.campaignType !== 'PD'
        ? `&campaign_type=${registrationConfig.campaignType}`
        : '';
    API.get(
      `/active_rates/?position_id=${position || ''}&entity_id=${rateEntity}${typeParam}&user_birth_date=${dayjs(
        registerLoginDone ? loginBirthDate : birthDate
      ).format('YYYY/MM/DD')}`
    )
      .then((response) => {
        const activeRates = parseActiveRatesResponse(response.data);
        setActiveRates(activeRates);

        if (changeStep) {
          setRegistrationStep(4);
        }
      })
      .catch((error) => {
        const msg = error.response?.data?.message;

        if (msg === 'Nessuna tariffa disponibile.') {
          // Campagna (GD o PD) senza tariffe per l'entità: il tesseramento non è completabile.
          setStep5Data({
            text: isGdMode
              ? 'Al momento non è possibile completare il tesseramento GD: nessuna tariffa disponibile per la campagna. Riprova più tardi o contatta l’helpdesk.'
              : 'Al momento non è possibile completare il tesseramento: nessuna tariffa disponibile. Riprova più tardi o contatta l’helpdesk.',
            icon: 'warning',
          });
        } else {
          // Include "Nessuna campagna disponibile…" e qualsiasi altro errore: nessun fallimento silenzioso.
          setStep5Data({
            text: msg || 'Si è verificato un errore durante il caricamento delle tariffe. Riprova più tardi.',
            icon: 'warning',
          });
        }

        // Vai direttamente all'esito (step 5): changeRegistrationStep non avanza dallo step 3
        // quando non c'è campagna/tariffa disponibile.
        setRegistrationStep(5);
      });
  };

  // Get Rates Total Value
  useEffect(() => {
    let ratesSum;
    if (!isGdMode && isGdAvailable) {
      ratesSum = activeRates.reduce((sum, rate) => sum + Number(rate.price_discounted), 0);
    } else {
      ratesSum = activeRates.reduce((sum, rate) => sum + Number(rate.price), 0);
    }
    setTotalPrice(ratesSum);
  }, [activeRates]);

  const [beneficiary, setBeneficiary] = useState('');
  const [ibanCode, setIbanCode] = useState('');
  const [causal, setCausal] = useState('');
  const [paymentNote, setPaymentNote] = useState('');

  // Get Bank Information
  const getBankData = () => {
    API.get('/paymentbank')
      .then((response) => {
        const data = response.data[0];
        setBeneficiary(data.intestatario);
        setIbanCode(data.iban);
        setCausal(data.causale);
        setPaymentNote(data.note);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false, false);
      });
  };

  const sendBankDataEmail = (e) => {
    e.preventDefault();

    API.post(`/paybanksendmail`, { id: membershipCard.id })
      .then((response) => {
        showPopup(true, false, response.data.message);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Generate Payment Causal Replacing Databse Placeholders
  const createCausal = (causal) => {
    const wNumber = causal.replace('%n', membershipCard.card_number);
    const addData = wNumber.replace('%d', dayjs(membershipCard.creation_date).format('DD/MM/YYYY'));

    return addData;
  };

  const [otherValue, setOtherValue] = useState({});

  const isSaveEnabled = !Number.isNaN(totalPrice) && totalPrice > 0;

  // Rates Changes
  const handleRateChange = (index, newValue) => {
    setActiveRates((prevRates) => {
      const newRates = [...prevRates];
      newRates[index].price = Number(newValue).toFixed(2).toString();
      newRates[index].price_discounted = Number(newValue).toFixed(2).toString();
      return newRates;
    });
  };

  // Rates Inputs Disabled
  const handleDisabledInput = (index) => {
    const newDisabledStates = [...disabledStates];
    newDisabledStates[index] = !newDisabledStates[index];
    setDisabledStates(newDisabledStates);
  };

  // payment operation finish handler
  const paymentDone = (paymentData) => {
    handleClosePayment();
    paymentFinishOperation(paymentData);
  };

  const paymentFinishOperation = (paymentData) => {
    let payedStatusID;

    paymentStatus.forEach((staus) => {
      if (staus.type === 'P') {
        payedStatusID = staus.id;
      }
    });

    const data = {
      user_id: userInfo.id,
      campaign_id: membershipCard.campaign_id,
      payment_status: payedStatusID,
      payment_type: membershipCard.payment_type,
      payment_entity_id: membershipCard.entity_id,
      payment_online: 1,
    };

    if (paymentData) {
      const date = new Date();
      date.setHours(date.getHours() + 2);
      const newIsoDate = date.toISOString();
      data.payment_date = newIsoDate;
      data.payment_pp_trans_id = paymentData.id;
      data.payment_pp_acc_name = paymentData.name;
      data.payment_pp_acc_surname = paymentData.surname;
      data.payment_pp_acc_mail = paymentData.email;
      data.payment_pp_acc_status = paymentData.status;
      data.payment_pp_acc_id = paymentData.payer_id;
    }

    API.put(`/memcards/${membershipCard.id}`, data)
      .then((response) => {
        changeRegistrationStep(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false, false);
      });
  };

  // payment close dialog handler
  const handleClosePayment = () => {
    setPaymentMethod({
      select: false,
      method: null,
      type: null,
    });
  };

  // create membership card request handler
  // Bundle PD+GD (R6/R17): dopo la creazione della tessera PD crea la tessera GD figlia
  // (importo 0 forzato lato server, parent = tessera PD). Attiva solo se "anche GD" è spuntato.
  const createGdChildCard = (pdCardId) => {
    if (!gdCheck || !gdEntity) return;

    // Tessera GD figlia gratuita (importo 0, parent = tessera PD): logica condivisa nel servizio.
    requestFreeLinkedGdCard({
      userId: userInfo.userID,
      parentCardId: pdCardId,
      gdEntityId: gdEntity.id,
      birthDate: registerLoginDone ? loginBirthDate : birthDate,
      digitalFormat: onlineMembershipCard,
    }).catch((error) => HttpErrorHandler(error, setPopUpText));
  };

  const onCreateMemCard = (e) => {
    e.preventDefault();

    const cardRates = {
      user_id: userInfo.userID,
      campaign_id: activeRates[0].campaign_id,
      rates: [],
      // GD-only: paga sull'entità master GD (coerente con le tariffe), non sull'entità PD.
      payment_entity_id: isGdMode && gdEntity ? gdEntity.id : userInfo.userEntity,
      payment_online: 1,
      digital_format: Number(onlineMembershipCard),
    };

    paymentStatus.forEach((status) => {
      if (status.type === 'W') {
        cardRates.payment_status = status.id;
      }
    });

    const items = [];
    const _paypalOrder = [];
    let _amount = 0;

    activeRates.forEach((element) => {
      if (element.price !== 0 && element.price !== 0.0 && element.price !== '0.00') {
        cardRates.rates.push({
          id: element.id,
          price: !isGdMode && isGdAvailable ? element.price_discounted : element.price,
        });

        // Paypal
        items.push({
          reference_id: element.id,
          amount: {
            currency_code: element.currency,
            value: !isGdMode && isGdAvailable ? element.price_discounted : element.price,
          },
          description: element.name,
        });

        if (isGdAvailable) {
          _amount += parseFloat(element.price_discounted);
        } else {
          _amount += parseFloat(element.price);
        }
      }
    });

    if (items.length !== 0) {
      API.post('/memcards', cardRates)
        .then((response) => {
          setPopUpText(response.data.message);
          showPopup(true, true);

          setCreatedCard(true);

          setMembershipCard({
            id: response.data.card.id,
            prefix: response.data.card.prefix,
            card_number: response.data.card.card_number,
            card_code: response.data.card.card_code,
            campaign_id: response.data.card.campaign.id,
            campaign_name: response.data.card.campaign.description,
            entity_name: response.data.card.payment_entity.name,
            entity_id: response.data.card.payment_entity.id,
            amount: _amount,
            currency: items[0].amount.currency_code,
            creation_date: response.data.card.creation_date,
            end_date: response.data.card.campaign.end_date,
            payment_status: response.data.card.payment_status.id,
            payment_type: null,
          });

          _paypalOrder.push({
            reference_id: response.data.card.card_number,
            amount: {
              currency_code: items[0].amount.currency_code,
              value: _amount,
            },
            description: `Tesseramento PD - ${
              response.data.card.card_code || `${response.data.card.prefix}${response.data.card.card_number}`
            }`,
          });

          setOrderItems(_paypalOrder);

          // Bundle: crea la tessera GD figlia collegata alla PD appena creata.
          createGdChildCard(response.data.card.id);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });

      handleCloseCreateCardDialog();
    } else {
      contextShowPopup('warning', 'Il contributo non può essere 0');
    }
  };

  // Skip card creation
  const skipCardCreation = () => {
    setRegistrationStep(5);
  };

  useEffect(() => {
    if (registrationStep === 5) {
      GlobalData.token = null;
      GlobalData.userData = null;
      GlobalData.loginData = null;
      localStorage.clear();
      sessionStorage.clear();
    }
  }, [registrationStep]);

  // Tessera GD (/join-gd): unico metodo ammesso è PayPal. Evitiamo lo step con l'unica card di
  // selezione (che appariva "vuoto"): selezioniamo automaticamente PayPal e mostriamo direttamente
  // il checkout (importo + pulsanti di pagamento).
  useEffect(() => {
    if (isGdMode && createdCard && !paymentMethod.select) {
      const paypalType = (Array.isArray(paymentTypes) ? paymentTypes : []).find((t) => t.type === 'PCC');
      if (paypalType) {
        membershipCard.payment_type = paypalType.id;
        setPaymentMethod({ select: true, method: paypalType.name, type: paypalType.type });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGdMode, createdCard, paymentMethod.select, paymentTypes]);

  const PaymentSection = (mobile) => {
    return (
      <>
        <Grid
          container
          sx={{
            justifyContent: mobile ? 'flex-start' : 'center',
            marginTop: mobile ? '24px' : '0',
            paddingLeft: '18px',
          }}
        >
          <Grid item xs={10}>
            <IconButton
              edge="start"
              color="inherit"
              sx={{ display: paymentMethod.select ? 'flex' : 'none', justifyContent: 'center', alignItems: 'center' }}
              onClick={() => setPaymentMethod({ select: false, method: null, type: null })}
              aria-label="back"
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          </Grid>

          <Grid
            item
            xs={12}
            md={10}
            sx={{ display: paymentMethod.select ? 'none' : 'flex', justifyContent: mobile ? 'flex-start' : 'center' }}
          >
            <Typography variant="h5" sx={{ textAlign: 'center' }}>
              {!paymentMethod.select ? 'Metodo di Pagamento' : paymentMethod.method}
            </Typography>
          </Grid>
        </Grid>

        {!paymentMethod.select ? (
          <Grid
            container
            rowGap={2}
            sx={{ justifyContent: 'center', marginTop: '24px', paddingLeft: mobile ? '18px' : '0' }}
          >
            {filterPaypalOnly(paymentTypes, isGdMode).map((type) => (
              <Grid key={type.id} item xs={12} md={10}>
                <PaymentMethodOption
                  type={type}
                  onSelect={(selected) => {
                    membershipCard.payment_type = selected.id;
                    setPaymentMethod({ select: true, method: selected.name, type: selected.type });
                  }}
                />
              </Grid>
            ))}
          </Grid>
        ) : paymentMethod.select && paymentMethod.type === 'PCC' ? (
          <Grid container sx={{ justifyContent: 'center', marginTop: '24px', paddingLeft: mobile ? '18px' : '0' }}>
            <Grid item xs={12} md={10}>
              <PaypalCheckout purchaseItems={orderItems} paymentDone={paymentDone} />
            </Grid>
          </Grid>
        ) : (
          // Bank transfer payment section
          <Grid container sx={{ justifyContent: 'center', marginTop: '24px', paddingLeft: mobile ? '18px' : '0' }}>
            <Grid item xs={12} md={10}>
              <BankTransferInfo
                beneficiary={beneficiary}
                iban={ibanCode}
                causale={membershipCard.card_code || `${membershipCard.prefix}${membershipCard.card_number}`}
                amount={totalPrice.toFixed(2)}
                currency={membershipCard.currency}
                note={paymentNote}
              />
            </Grid>
          </Grid>
        )}

        <Grid container sx={{ justifyContent: 'center', marginTop: '24px' }}>
          <Grid item xs={12} md={10} sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: '8px' }}>
            <Tooltip title="Copia dati" arrow placement="top">
              <Box>
                <CustomIconButton
                  sx={{
                    display: paymentMethod.type === 'BB' ? 'block' : 'none',
                    height: '40px',
                    width: '40px',
                  }}
                  onClick={() => {
                    const copyText = navigator.clipboard.writeText(
                      `Beneficiario: ${beneficiary}\nIBAN: ${ibanCode}\nCausale: ${createCausal(
                        causal
                      )}\nImporto: ${totalPrice.toFixed(2)} ${membershipCard.currency}`
                    );

                    contextShowPopup('info', 'Copiato con successo');

                    return copyText;
                  }}
                >
                  <ContentCopy />
                </CustomIconButton>
              </Box>
            </Tooltip>

            {paymentMethod.type === 'BB' && (
              <Tooltip title="Invia dati via mail" arrow placement="top">
                <Box>
                  <CustomIconButton onClick={sendBankDataEmail}>
                    <EmailOutlinedIcon />
                  </CustomIconButton>
                </Box>
              </Tooltip>
            )}

            <DefaultOutlinedButton
              customStyles={{
                display: membershipCard.id !== null ? 'block' : 'none',
              }}
              onClick={() => changeRegistrationStep(true)}
            >
              Chiudi
            </DefaultOutlinedButton>

            {/* <DefaultContainedButton
        customStyles={{
          marginTop: '20px',
          marginLeft: '20px',
          display: membershipCard.id !== null ? 'block' : 'none',
        }}
        onClick={handleOpenPayment}
      >
        Procedi al pagamento
      </DefaultContainedButton> */}
          </Grid>
        </Grid>
      </>
    );
  };

  // Stili condivisi della landing (step 0) — card di scelta tesseramento pulite e coerenti col tema HUB.
  const membershipCardSx = {
    height: '100%',
    borderRadius: 2,
    borderColor: 'divider',
    transition: 'border-color .2s ease, box-shadow .2s ease, transform .2s ease',
    '&:hover': { borderColor: 'common.black', boxShadow: 6, transform: 'translateY(-2px)' },
  };
  const membershipContentSx = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    p: 3,
    textAlign: 'left',
  };
  const membershipCtaSx = {
    mt: 2.5,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    color: 'primary.main',
    fontWeight: 700,
    fontSize: 14,
  };

  return (
    <>
      <Box width="100%" height="100%" sx={{ position: 'fixed', zIndex: '-1' }} className="default-bg" />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{ backgroundColor: 'white', maxHeight: '150px', display: isVolounteerRegistration ? 'none' : 'flex' }}
      >
        <Box component="img" src={registrationConfig.header.imageSrc} sx={{ maxHeight: '150px' }} />
      </Stack>

      <Stack direction="column" justifyContent="center" alignItems="center" sx={{ minHeight: 'calc(100vh - 150px)' }}>
        <Box
          sx={{
            margin: is600Screen ? '25px 0px' : '0',
            padding: is1200Screen ? '24px 24px' : '24px 18px',
            borderRadius: is600Screen ? '8px' : '0',
            backgroundColor: 'white',
            boxShadow: is600Screen && '0px 0px 21px 3px rgba(0, 0, 0, 0.5)',
            overflow: 'auto',
            display: registrationStep === 1 && isRenewRegistration && !registerLoginDone ? 'none' : 'block',
          }}
          width={is1200Screen ? '950px' : is900Screen ? '700px' : is600Screen ? '580px' : '100%'}
          minHeight={is600Screen ? 'auto' : '100vh'}
          height={is600Screen ? 'auto' : '100%'}
        >
          <Backdrop sx={{ color: '#fff', zIndex: '999' }} open={openBackdrop}>
            <CircularProgress />
          </Backdrop>

          {/* Registration step 0 | select registration  */}
          {registrationStep === 0 && (
            <Box>
              {italyApiCalled ? (
                <Box sx={{ maxWidth: 760, mx: 'auto', width: '100%', py: is600Screen ? 5 : 3 }}>
                  {/* Hero */}
                  <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      Portale Tesseramento
                    </Typography>
                    <Typography sx={{ mt: 1.5, color: 'text.secondary', maxWidth: 460, mx: 'auto' }}>
                      Scegli come partecipare: iscriviti, rinnova la tua tessera o unisciti alla comunità.
                    </Typography>
                    <Divider
                      sx={{
                        width: 48,
                        mx: 'auto',
                        mt: 3,
                        borderBottomWidth: 3,
                        borderColor: 'primary.main',
                        borderRadius: 2,
                      }}
                    />
                  </Box>

                  {/* Scelta primaria: percorsi di tesseramento */}
                  <Grid container spacing={2.5} justifyContent="center">
                    <Grid item xs={12} sm={isGdMode ? 12 : 4}>
                      <Card variant="outlined" sx={membershipCardSx} onClick={handleNewRegistration}>
                        <CardActionArea sx={{ height: '100%' }}>
                          <CardContent sx={membershipContentSx}>
                            <Box
                              component="img"
                              src={isGdMode ? '/assets/images/img/gd_logo.png' : '/assets/images/img/logopd.png'}
                              alt=""
                              sx={{ height: 48, width: 'auto', objectFit: 'contain', alignSelf: 'flex-start' }}
                            />
                            <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>
                              {isGdMode ? 'Iscriviti ai Giovani Democratici' : 'Iscriviti al Partito Democratico'}
                            </Typography>
                            <Typography sx={{ mt: 0.5, color: 'text.secondary', flexGrow: 1 }}>
                              {isGdMode
                                ? 'Compila la domanda e tesserati ai Giovani Democratici.'
                                : 'Compila la domanda e tesserati al Partito Democratico.'}
                            </Typography>
                            <Box sx={membershipCtaSx}>
                              Inizia l&apos;iscrizione
                              <ArrowForwardIcon sx={{ fontSize: 18 }} />
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>

                    {!isGdMode && (
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined" sx={membershipCardSx} onClick={() => navigate('/join-gd')}>
                          <CardActionArea sx={{ height: '100%' }}>
                            <CardContent sx={membershipContentSx}>
                              <Box
                                component="img"
                                src="/assets/images/img/gd_logo.png"
                                alt=""
                                sx={{ height: 48, width: 'auto', objectFit: 'contain', alignSelf: 'flex-start' }}
                              />
                              <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>
                                Iscriviti ai Giovani Democratici
                              </Typography>
                              <Typography sx={{ mt: 0.5, color: 'text.secondary', flexGrow: 1 }}>
                                Tesseramento riservato a chi ha tra 14 e 29 anni.
                              </Typography>
                              <Box sx={membershipCtaSx}>
                                Vai all&apos;iscrizione GD
                                <ArrowForwardIcon sx={{ fontSize: 18 }} />
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    )}

                    {/* Iscrizione contestuale PD + GD */}
                    {!isGdMode && (
                      <Grid item xs={12} sm={4}>
                        <Card
                          variant="outlined"
                          sx={membershipCardSx}
                          onClick={(e) => {
                            handleNewRegistration(e);
                            setGdCheck(true);
                          }}
                        >
                          <CardActionArea sx={{ height: '100%' }}>
                            <CardContent sx={membershipContentSx}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, alignSelf: 'flex-start' }}>
                                <Box
                                  component="img"
                                  src="/assets/images/img/logopd.png"
                                  alt=""
                                  sx={{ height: 48, width: 'auto', objectFit: 'contain' }}
                                />
                                <Box
                                  component="img"
                                  src="/assets/images/img/gd_logo.png"
                                  alt=""
                                  sx={{ height: 48, width: 'auto', objectFit: 'contain' }}
                                />
                              </Box>
                              <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>
                                Iscriviti al Pd e ai GD
                              </Typography>
                              <Typography sx={{ mt: 0.5, color: 'text.secondary', flexGrow: 1 }}>
                                Se hai tra i 16 e i 29 anni puoi iscriverti a PD e GD contestualmente.
                              </Typography>
                              <Box sx={membershipCtaSx}>
                                Inizia l&apos;iscrizione
                                <ArrowForwardIcon sx={{ fontSize: 18 }} />
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    )}
                  </Grid>

                  {/* Scelta secondaria: partecipa senza tesserarti */}
                  <Card
                    variant="outlined"
                    sx={{
                      mt: 2.5,
                      borderRadius: 2,
                      borderStyle: 'dashed',
                      borderColor: 'divider',
                      transition: 'border-color .2s ease',
                      '&:hover': { borderColor: 'text.secondary' },
                    }}
                    onClick={(e) => {
                      handleNewRegistration(e);
                      setIsVolunteerRegistration(true);
                    }}
                  >
                    <CardActionArea>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }}>
                        <Box
                          sx={{
                            flexShrink: 0,
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'action.hover',
                            color: 'text.secondary',
                          }}
                        >
                          <GroupsIcon />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Partecipa senza tesserarti
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Registrati alla comunità del PD e diventa volontario/a. Potrai tesserarti più avanti.
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>

                  {/* Footer: accesso e rinnovo */}
                  <Box
                    sx={{
                      mt: 4,
                      pt: 3,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: is600Screen ? 'row' : 'column',
                      alignItems: is600Screen ? 'center' : 'flex-start',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>Hai già una tessera?</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Accedi al portale HUB per rinnovare la tessera o gestire il tuo profilo.
                      </Typography>
                    </Box>
                    <DefaultOutlinedButton onClick={() => navigate('/')}>Accedi</DefaultOutlinedButton>
                  </Box>
                </Box>
              ) : (
                LoadingUI()
              )}
            </Box>
          )}

          {registrationStep !== 0 && registrationStep !== 5 && (
            <Grid container justifyContent="center" alignItems="center" spacing={3} sx={{ paddingBottom: '24px' }}>
              <Grid item xs={12} lg={10}>
                <Button onClick={() => navigate('/')} variant="text" color="secondary" startIcon={<HomeIcon />}>
                  Vai alla pagina di login
                </Button>
              </Grid>
            </Grid>
          )}
          {/* registration step 1 | user data */}
          {registrationStep === 1 && (isNewRegistration || isRenewRegistration) && (
            <Box>
              {/* Stepper */}
              <Box sx={{ width: '100%', marginBottom: '32px' }}>
                <Stepper activeStep={registrationStep - 1} alternativeLabel>
                  <Step key={1}>
                    <StepLabel>Anagrafica</StepLabel>
                  </Step>
                  {!registerLoginDone && (
                    <Step key={2}>
                      <StepLabel>Verifica</StepLabel>
                    </Step>
                  )}
                  {!registerLoginDone && (
                    <Step key={3}>
                      <StepLabel>Password</StepLabel>
                    </Step>
                  )}

                  {!isVolounteerRegistration && (
                    <Step key={4}>
                      <StepLabel>Tesseramento</StepLabel>
                    </Step>
                  )}
                </Stepper>
              </Box>

              {/* Avviso informativo tesseramento GD */}
              {isGdMode && (
                <Grid justifyContent="center" alignItems="center" container sx={{ mb: 3 }}>
                  <Grid item xs={12} md={12} lg={10}>
                    <Alert severity="info">
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Proseguendo su questa pagina potrai iscriverti ai Giovani Democratici al costo di{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          5€
                        </Box>
                        .
                      </Typography>
                      <Typography variant="body2">
                        💡 Vuoi iscriverti anche al Partito Democratico? Se desideri la doppia tessera (PD + GD),
                        effettua la registrazione dal{' '}
                        <Link component={RouterLink} to="/join" state={{ startAtStep1: true }}>
                          portale di iscrizione del PD
                        </Link>
                        . In questo modo, l&apos;iscrizione ai GD sarà inclusa automaticamente senza costi aggiuntivi!
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              )}

              {/* User Information Form */}
              <form method="post" id="register-form" onSubmit={onRegisterUser}>
                <Box>
                  <Grid justifyContent="center" alignItems="center" container spacing={3}>
                    {/* Name */}
                    <Grid item xs={12} md={6} lg={5}>
                      {registerLoginDone ? (
                        <div>
                          <CustomTextField
                            InputProps={{
                              readOnly: true,
                            }}
                            name="Login-Name"
                            label="Nome"
                            value={loginName}
                          />
                        </div>
                      ) : (
                        <CustomTextField error={errors.name} id="register-name" name="name" label="Nome" />
                      )}
                    </Grid>

                    {/* Surname */}
                    <Grid item xs={12} md={6} lg={5}>
                      {registerLoginDone ? (
                        <div>
                          <CustomTextField
                            InputProps={{
                              readOnly: true,
                            }}
                            name="Login-Surname"
                            label="Cognome"
                            value={loginSurname}
                          />
                        </div>
                      ) : (
                        <CustomTextField error={errors.surname} id="register-surname" name="surname" label="Cognome" />
                      )}
                    </Grid>

                    <Grid item xs={12} lg={10}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={registerLoginDone}
                            checked={registerLoginDone ? !!loginUseAlias : !!useAlias}
                            onChange={(e) => {
                              setUseAlias(e.target.checked);
                            }}
                          />
                        }
                        label="Utilizza nome alias"
                      />
                    </Grid>

                    {useAlias && (
                      <Grid item xs={12} lg={10}>
                        <Grid
                          container
                          spacing={3}
                          style={{ opacity: aliasDynamicContainer, transition: 'opacity 1s' }}
                        >
                          <Grid item xs={8}>
                            {registerLoginDone ? (
                              <div>
                                <CustomTextField
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  name="Login-Alias"
                                  label="Alias"
                                  value={loginAlias}
                                />
                              </div>
                            ) : (
                              <CustomTextField
                                error={errors.name_alias}
                                id="register-alias"
                                name="alias"
                                label="Alias"
                                onChange={(e) => setAlias(e.currentTarget.value)}
                              />
                            )}
                          </Grid>

                          {/* Gender */}
                          <Grid item xs={4}>
                            {registerLoginDone ? (
                              <CustomTextField
                                InputProps={{
                                  readOnly: true,
                                }}
                                name="Login-Gender"
                                label="Sesso *"
                                value={loginGender}
                              />
                            ) : (
                              <CustomAutocomplete
                                required={useAlias}
                                id="register-gender"
                                options={genderList}
                                isOptionEqualToValue={(option, value) => option.gender === value.gender}
                                getOptionLabel={(option) => option.gender}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} key={option.gender}>
                                    {option.gender}
                                  </Box>
                                )}
                                onChange={(e, value) => {
                                  setGender(value);
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} label="Sesso" name="gender" variant="outlined" />
                                )}
                              />
                            )}
                          </Grid>

                          <Grid item xs={12}>
                            <Alert severity="warning">
                              Specificare un "Nome Alias" e il "Sesso" solo se si è in una fase di transizione di
                              genere.
                            </Alert>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}

                    {/* Date of Birth */}
                    <Grid item xs={12} md={6} lg={5}>
                      <Box>
                        {registerLoginDone ? (
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                            <CustomDatePicker
                              readOnly
                              id="login-birthdate"
                              label="Data di Nascita"
                              value={loginBirthDate}
                              format="DD/MM/YYYY"
                            />
                          </LocalizationProvider>
                        ) : (
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                            <CustomDatePicker
                              error={errors.birth_date}
                              required
                              id="register-birthdate"
                              label="Data di Nascita *"
                              onChange={handleBirthdateChange}
                              format="DD/MM/YYYY"
                              maxDate={minAgeCutoff}
                            />
                          </LocalizationProvider>
                        )}
                      </Box>
                    </Grid>

                    {/* Birth Place */}
                    <Grid item xs={12} md={6} lg={5}>
                      {registerLoginDone ? (
                        <CustomTextField
                          InputProps={{
                            readOnly: true,
                          }}
                          name="Login-BirthPlace"
                          label="Luogo di Nascita"
                          value={loginBirthPlace.name}
                        />
                      ) : (
                        <CustomAutocomplete
                          required
                          options={birthPlacesList}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          id="register-birth-city"
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option?.province_abbr?.length > 0
                                ? `${option.name} (${option?.province_abbr})`
                                : option.name}
                            </Box>
                          )}
                          value={birthPlace}
                          onChange={(e, value) => setBirthPlace(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.place_of_birth}
                              label="Luogo di Nascita *"
                              variant="outlined"
                            />
                          )}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} lg={10}>
                      <Alert severity="warning">
                        Attenzione! Verrà verificata la compatibilità del codice fiscale con i tuoi dati anagrafici. Se
                        riscontri problemi contatta l'helpdesk all'indirizzo tesseramento@partitodemocratico.it
                      </Alert>
                    </Grid>

                    {/* Fiscalcode */}
                    <Grid item xs={12} md={6} lg={4}>
                      {registerLoginDone ? (
                        <div>
                          <CustomTextField
                            InputProps={{
                              readOnly: true,
                            }}
                            name="Login-FiscalCode"
                            label="Codice Fiscale"
                            value={loginFiscalCode}
                          />
                        </div>
                      ) : (
                        <CustomTextField
                          error={errors.fiscal_code}
                          id="register-fiscal-code"
                          name="fiscal-code"
                          label="Codice Fiscale"
                          onChange={(e) => setUserFiscalCode(e.currentTarget.value)}
                        />
                      )}
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12} md={6} lg={4}>
                      {registerLoginDone ? (
                        <div>
                          <CustomTextField
                            InputProps={{
                              readOnly: true,
                            }}
                            name="Login-Email"
                            label="Email"
                            value={loginEmail}
                          />
                        </div>
                      ) : (
                        <CustomTextField
                          error={errors.email}
                          id="register-email"
                          name="email"
                          label="E-mail"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip arrow title="Riceverai un codice di verifica" placement="top">
                                  <InfoOutlinedIcon sx={{ color: '#2f58ca' }} />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          onChange={(event) => setEmailAddress(event.currentTarget.value)}
                        />
                      )}
                    </Grid>

                    {/* Call Code */}
                    <Grid item xs={4} md={2}>
                      {registerLoginDone ? (
                        <CustomTextField
                          InputProps={{
                            readOnly: true,
                          }}
                          name="Login-CallCode"
                          label="Prefisso"
                          value={loginCallCode.call_code}
                        />
                      ) : (
                        <CustomAutocomplete
                          required
                          options={resCountriesList}
                          id="register-phone-prefix"
                          getOptionLabel={(option) => option.call_code}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name} {option.call_code}
                            </Box>
                          )}
                          value={registerLoginDone ? loginCallCode : callCode}
                          filterOptions={(options, state) => {
                            const newOptions = [];
                            options.forEach((element) => {
                              if (
                                element.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                                element.call_code.toLowerCase().includes(state.inputValue.toLowerCase())
                              ) {
                                newOptions.push(element);
                              }
                            });
                            return newOptions;
                          }}
                          onChange={(e, value) => setCallCode(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.call_code}
                              label="Prefisso *"
                              name="call_code"
                              variant="outlined"
                            />
                          )}
                        />
                      )}
                    </Grid>

                    {/* Phone Number */}
                    <Grid item xs={8} md={5} lg={4}>
                      {registerLoginDone ? (
                        <div>
                          <CustomTextField
                            InputProps={{
                              readOnly: true,
                            }}
                            name="Login-Phone"
                            label="Cellulare"
                            value={loginPhone}
                          />
                        </div>
                      ) : (
                        <CustomTextField
                          error={errors.phone}
                          type="tel"
                          pattern="[0-9]{12}"
                          id="register-phone-number"
                          name="phone-number"
                          label="Cellulare"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip arrow title="Riceverai un codice di verifica" placement="top">
                                  <InfoOutlinedIcon sx={{ color: '#2f58ca' }} />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Backspace' && e.key !== 'Tab' && !/[0-9\b]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(event) => validatePhoneNumber(event, setPhoneNumber)}
                        />
                      )}
                    </Grid>

                    {/* Residence Country */}
                    <Grid item xs={12} md={5} lg={3}>
                      {registerLoginDone ? (
                        <div>
                          <CustomAutocomplete
                            required
                            options={resCountriesList.filter(
                              (country) =>
                                (!loginResRegion || country.id === loginResRegion.country_id) &&
                                (!loginResProvince || country.id === loginResProvince.country_id)
                            )}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={loginResCountry}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.id}>
                                {option.name}
                              </Box>
                            )}
                            onChange={(e, value) => setLoginResCountry(value)}
                            renderInput={(params) => <TextField {...params} label="Paese *" variant="outlined" />}
                          />
                        </div>
                      ) : (
                        <CustomAutocomplete
                          required
                          options={resCountriesList.filter(
                            (country) =>
                              (!resRegion || country.id === resRegion.country_id) &&
                              (!resProvince || country.id === resProvince.country_id)
                          )}
                          getOptionLabel={(option) => option.name}
                          value={resCountry}
                          id="register-res-country"
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => setResCountry(value)}
                          renderInput={(params) => (
                            <TextField {...params} error={errors.res_country_id} label="Paese *" variant="outlined" />
                          )}
                        />
                      )}
                    </Grid>

                    {/* Residence Region */}
                    <Grid item xs={12} md={6} lg={3}>
                      {registerLoginDone ? (
                        <div>
                          <CustomAutocomplete
                            required
                            options={resRegionList.filter(
                              (region) =>
                                (!loginResProvince || region.region_name === loginResProvince.region_name) &&
                                (!loginResCountry || region.country_id === loginResCountry.id)
                            )}
                            isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                            getOptionLabel={(option) => option.region_name}
                            id="register-res-region"
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.region_name}>
                                {option.region_name}
                              </Box>
                            )}
                            value={loginResRegion}
                            onChange={(e, value) => setLoginResRegion(value)}
                            renderInput={(params) => <TextField {...params} label="Regione *" variant="outlined" />}
                          />
                        </div>
                      ) : (
                        <CustomAutocomplete
                          freeSolo
                          forcePopupIcon={resCountry === italy}
                          required
                          options={resRegionList.filter(
                            (region) =>
                              (!resProvince || region.region_name === resProvince.region_name) &&
                              (!resCountry || region.country_id === resCountry.id)
                          )}
                          isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            if (option.inputValue) {
                              return option.inputValue;
                            }
                            return option.region_name;
                          }}
                          id="register-res-region"
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.region_name}>
                              {option.region_name}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            if (typeof value === 'string') {
                              setResRegion(value);
                            } else if (value && value.inputValue) {
                              setResRegion(value.inputValue);
                            } else {
                              setResRegion(value);
                            }
                          }}
                          onInputChange={(e, value) => {
                            setResRegionName(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.res_region_name || errors.res_region_id}
                              label="Regione *"
                              variant="outlined"
                            />
                          )}
                        />
                      )}
                    </Grid>

                    {/* Residence Province */}
                    <Grid item xs={12} md={6} lg={5}>
                      {registerLoginDone ? (
                        <div>
                          <CustomAutocomplete
                            required
                            options={resProvincesList.filter(
                              (province) =>
                                (!loginResRegion || province.region_name === loginResRegion.region_name) &&
                                (!loginResCity || province.id === loginResCity.province_id) &&
                                (!loginResCountry || province.country_id === loginResCountry.id)
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            id="register-res-province"
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.id}>
                                {option.name}
                              </Box>
                            )}
                            value={loginResProvince}
                            onChange={(e, value) => setLoginResProvince(value)}
                            renderInput={(params) => <TextField {...params} label="Provincia *" variant="outlined" />}
                          />
                        </div>
                      ) : (
                        <CustomAutocomplete
                          freeSolo
                          forcePopupIcon={resCountry?.name === italy?.name}
                          required={resCountry?.name === italy?.name}
                          disabled={resCountry?.name !== italy?.name}
                          options={resProvincesList.filter(
                            (province) =>
                              (!resRegion || province.region_name === resRegion.region_name) &&
                              (!resCity || province.id === resCity.province_id) &&
                              (!resCountry || province.country_id === resCountry.id)
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          id="register-res-province"
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            if (option.inputValue) {
                              return option.inputValue;
                            }
                            return option.name;
                          }}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            if (typeof value === 'string') {
                              setResProvince(value);
                            } else if (value && value.inputValue) {
                              setResProvince(value.inputValue);
                            } else {
                              setResProvince(value);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.res_province_id}
                              label="Provincia *"
                              variant="outlined"
                            />
                          )}
                        />
                      )}
                    </Grid>

                    {/* Residence City */}
                    <Grid item xs={12} md={5} lg={5}>
                      {registerLoginDone ? (
                        <div>
                          <CustomAutocomplete
                            required
                            options={resCitiesList.filter(
                              (city) => !loginResProvince || city.province_id === loginResProvince.id
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={loginResCity}
                            id="register-res-city"
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.id}>
                                {option.name}
                              </Box>
                            )}
                            onChange={(e, value) => setLoginResCity(value)}
                            renderInput={(params) => <TextField {...params} label="Comune *" variant="outlined" />}
                          />
                        </div>
                      ) : (
                        <CustomAutocomplete
                          freeSolo
                          forcePopupIcon={resCountry === italy}
                          required
                          options={resCitiesList.filter((city) => !resProvince || city.province_id === resProvince.id)}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          id="register-res-city"
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            if (option.inputValue) {
                              return option.inputValue;
                            }
                            return option.name;
                          }}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            if (typeof value === 'string') {
                              setResCity(value);
                            } else if (value && value.inputValue) {
                              setResCity(value.inputValue);
                            } else {
                              setResCity(value);
                            }
                          }}
                          onInputChange={(e, value) => {
                            setResCityName(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.res_city_name || errors.res_city_id}
                              label="Comune *"
                              variant="outlined"
                            />
                          )}
                        />
                      )}
                    </Grid>

                    {/* Residence Street */}
                    <Grid item xs={12} md={5} lg={5}>
                      {registerLoginDone > 0 ? (
                        <div>
                          <CustomTextField
                            name="Login-ResStreet"
                            label="Indirizzo e N. Civico"
                            value={loginResStreet}
                            onChange={(e) => {
                              setLoginResStreet(e.target.value);
                            }}
                          />
                        </div>
                      ) : (
                        <CustomTextField
                          id="register-res-address"
                          error={errors.res_street}
                          name="res-adress"
                          onChange={(e) => {
                            setResStreet(e.target.value);
                          }}
                          label="Indirizzo e N. Civico"
                        />
                      )}
                    </Grid>

                    {/* Residence Postal Code */}
                    <Grid item xs={12} md={2} lg={5}>
                      {registerLoginDone > 0 ? (
                        <div>
                          <CustomTextField
                            required
                            onChange={(e) => {
                              setLoginResZipCode(e.target.value);
                            }}
                            name="Login-ZipCode"
                            label="CAP"
                            value={loginResZipCode}
                          />
                        </div>
                      ) : (
                        <CustomTextField
                          error={errors.res_zip_code}
                          id="register-res-cap"
                          name="res-cap"
                          label="CAP"
                          inputProps={{
                            maxLength: 15,
                          }}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} lg={10}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={registerLoginDone ? !!loginResEqualHome : !!domRes}
                            onChange={HandleDomRes}
                          />
                        }
                        label="Il domicilio corrisponde con la residenza"
                      />
                    </Grid>

                    {/* Domicile != Residence */}
                    {domRes === false && (
                      <Grid style={{ opacity: domDynamicContainer, transition: 'opacity 1s' }} item xs={12}>
                        <Grid container justifyContent="center" alignItems="center" spacing={3}>
                          <Grid item xs={12} lg={10}>
                            <Typography sx={{ fontWeight: '500', textDecoration: 'underline' }}>
                              Inserisci di seguito le informazioni relative al domicilio
                            </Typography>
                          </Grid>

                          {/* Domicile Country */}
                          <Grid item xs={12} md={4} lg={3}>
                            {registerLoginDone > 0 ? (
                              <div>
                                <CustomAutocomplete
                                  options={domCountriesList.filter(
                                    (country) =>
                                      (!loginDomRegion || country.id === loginDomRegion.country_id) &&
                                      (!loginDomProvince || country.id === loginDomProvince.country_id)
                                  )}
                                  getOptionLabel={(option) => option.name}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  renderOption={(props, option) => (
                                    <Box component="li" id="country-id" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  value={loginDomCountry}
                                  onChange={(e, value) => setLoginDomCountry(value)}
                                  renderInput={(params) => <TextField {...params} label="Paese" variant="outlined" />}
                                />
                              </div>
                            ) : (
                              <CustomAutocomplete
                                options={domCountriesList.filter(
                                  (country) =>
                                    (!domRegion || country.id === domRegion.country_id) &&
                                    (!domProvince || country.id === domProvince.country_id)
                                )}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                id="register-dom-country"
                                renderOption={(props, option) => (
                                  <Box component="li" id="country-id" {...props} key={option.id}>
                                    {option.name}
                                  </Box>
                                )}
                                onChange={(e, value) => setDomCountry(value)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={errors.home_country_id}
                                    label="Paese *"
                                    variant="outlined"
                                  />
                                )}
                              />
                            )}
                          </Grid>

                          {/* Domicile Region */}
                          <Grid item xs={12} md={4} lg={3}>
                            {registerLoginDone > 0 ? (
                              <div>
                                <CustomAutocomplete
                                  options={domRegionList.filter(
                                    (region) =>
                                      (!loginDomProvince || region.region_name === loginDomProvince.region_name) &&
                                      (!loginDomCountry || region.country_id === loginDomCountry.id)
                                  )}
                                  isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                                  getOptionLabel={(option) => option.region_name}
                                  id="register-dom-region"
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.region_name}>
                                      {option.region_name}
                                    </Box>
                                  )}
                                  value={loginDomRegion}
                                  onChange={(e, value) => setLoginDomRegion(value)}
                                  renderInput={(params) => <TextField {...params} label="Regione" variant="outlined" />}
                                />
                              </div>
                            ) : (
                              <CustomAutocomplete
                                freeSolo
                                forcePopupIcon={domCountry === italy}
                                options={domRegionList.filter(
                                  (region) =>
                                    (!domProvince || region.region_name === domProvince.region_name) &&
                                    (!domCountry || region.country_id === domCountry.id)
                                )}
                                isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                                getOptionLabel={(option) => {
                                  if (typeof option === 'string') {
                                    return option;
                                  }
                                  if (option.inputValue) {
                                    return option.inputValue;
                                  }
                                  return option.region_name || '';
                                }}
                                id="register-dom-region"
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} key={option.region_name}>
                                    {option.region_name}
                                  </Box>
                                )}
                                onChange={(e, value) => {
                                  if (typeof value === 'string') {
                                    setDomRegion(value);
                                  } else if (value && value.inputValue) {
                                    setDomRegion(value.inputValue);
                                  } else {
                                    setDomRegion(value);
                                  }
                                }}
                                onInputChange={(e, value) => {
                                  setDomRegionName(value);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={errors.home_region_id || errors.home_region_name}
                                    label="Regione *"
                                    variant="outlined"
                                  />
                                )}
                              />
                            )}
                          </Grid>

                          {/* Domicile Province */}
                          <Grid item xs={12} md={4} lg={4}>
                            {registerLoginDone > 0 ? (
                              <div>
                                <CustomAutocomplete
                                  options={domProvincesList.filter(
                                    (province) =>
                                      (!loginDomRegion || province.region_name === loginDomRegion.region_name) &&
                                      (!loginDomCity || province.id === loginDomCity.province_id) &&
                                      (!loginDomCountry || province.country_id === loginDomCountry.id)
                                  )}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  id="register-dom-province"
                                  getOptionLabel={(option) => option.name}
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  value={loginDomProvince}
                                  onChange={(e, value) => setLoginDomProvince(value)}
                                  renderInput={(params) => (
                                    <TextField {...params} label="Provincia" variant="outlined" />
                                  )}
                                />
                              </div>
                            ) : (
                              <CustomAutocomplete
                                freeSolo
                                forcePopupIcon={domCountry === italy}
                                options={domProvincesList.filter(
                                  (province) =>
                                    (!domRegion || province.region_name === domRegion.region_name) &&
                                    (!domCity || province.id === domCity.province_id) &&
                                    (!domCountry || province.country_id === domCountry.id)
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                id="register-dom-province"
                                getOptionLabel={(option) => {
                                  if (typeof option === 'string') {
                                    return option;
                                  }
                                  if (option.inputValue) {
                                    return option.inputValue;
                                  }
                                  return option.name || '';
                                }}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} key={option.id}>
                                    {option.name}
                                  </Box>
                                )}
                                onChange={(e, value) => {
                                  if (typeof value === 'string') {
                                    setDomProvince(value);
                                  } else if (value && value.inputValue) {
                                    setDomProvince(value.inputValue);
                                  } else {
                                    setDomProvince(value);
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={errors.home_province_id}
                                    label="Provincia *"
                                    variant="outlined"
                                  />
                                )}
                              />
                            )}
                          </Grid>

                          {/* Domicile City */}
                          <Grid item xs={12} md={5} lg={4}>
                            {registerLoginDone > 0 ? (
                              <div>
                                <CustomAutocomplete
                                  options={domCitiesList.filter((city) =>
                                    loginDomProvince ? city.province_id === loginDomProvince.id : true
                                  )}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  id="register-dom-city"
                                  getOptionLabel={(option) => option.name}
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  value={loginDomCity}
                                  onChange={(e, value) => setLoginDomCity(value)}
                                  renderInput={(params) => <TextField {...params} label="Comune" variant="outlined" />}
                                />
                              </div>
                            ) : (
                              <CustomAutocomplete
                                freeSolo
                                forcePopupIcon={domCountry === italy}
                                options={domCitiesList.filter((city) =>
                                  domProvince ? city.province_id === domProvince.id : true
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                id="register-dom-city"
                                getOptionLabel={(option) => {
                                  if (typeof option === 'string') {
                                    return option;
                                  }
                                  if (option.inputValue) {
                                    return option.inputValue;
                                  }
                                  return option.name || '';
                                }}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} key={option.id}>
                                    {option.name}
                                  </Box>
                                )}
                                onChange={(e, value) => {
                                  if (typeof value === 'string') {
                                    setDomCity(value);
                                  } else if (value && value.inputValue) {
                                    setDomCity(value.inputValue);
                                  } else {
                                    setDomCity(value);
                                  }
                                }}
                                onInputChange={(e, value) => {
                                  setDomCityName(value);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={errors.home_city_id || errors.home_city_name}
                                    label="Comune *"
                                    variant="outlined"
                                  />
                                )}
                              />
                            )}
                          </Grid>

                          {/* Domicile Street */}
                          <Grid item xs={12} md={5} lg={4}>
                            {registerLoginDone > 0 ? (
                              <div>
                                <CustomTextField
                                  required
                                  name="Login-DomStreet"
                                  label="Indirizzo"
                                  value={loginDomStreet}
                                  onChange={(e) => {
                                    setLoginDomStreet(e.target.value);
                                  }}
                                />
                              </div>
                            ) : (
                              <CustomTextField
                                id="register-dom-adress"
                                error={errors.home_zip_code}
                                onChange={(e) => {
                                  setDomStreet(e.target.value);
                                }}
                                name="dom-adress"
                                label="Indirizzo *"
                              />
                            )}
                          </Grid>

                          {/* Domicile zipCode */}
                          <Grid item xs={12} md={2}>
                            {registerLoginDone > 0 ? (
                              <div>
                                <CustomTextField
                                  required
                                  name="Login-DomZipCode"
                                  label="CAP"
                                  value={loginDomZipCode}
                                  onChange={(e) => {
                                    setLoginDomZipCode(e.target.value);
                                  }}
                                />
                              </div>
                            ) : (
                              <CustomTextField
                                error={errors.home_zip_code}
                                id="register-dom-cap"
                                name="dom-cap"
                                label="CAP *"
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    )}

                    <Grid item xs={12} lg={10}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={entitiesFilterByResidence}
                            onChange={() => setEntitiesFilterByResidence(!entitiesFilterByResidence)}
                            disabled={domRes}
                          />
                        }
                        label={
                          entitiesFilterByResidence
                            ? 'Circoli filtrati per RESIDENZA'
                            : 'Circoli filtrati per DOMICILIO'
                        }
                      />
                    </Grid>

                    <Grid item xs={12} lg={10} sx={{ display: isVolounteerRegistration ? 'flex' : 'none' }}>
                      <Alert severity="warning">
                        Scegli un circolo di riferimento, in modo che circolo e Federazione PD possano aggiornarti sulle
                        attività che vengono promosse sul tuo territorio.
                      </Alert>
                    </Grid>

                    {/* Entity */}
                    <Grid item xs={12} md={12} lg={10} sx={{ order: 1 }}>
                      {registerLoginDone > 0 ? (
                        <div>
                          <CustomAutocomplete
                            required
                            disabled={disableEntity}
                            id="register-entity"
                            options={EntitiesList}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.id}>
                                {option.name}
                              </Box>
                            )}
                            value={loginEntity}
                            onChange={(e, value) => {
                              setLoginEntity(value);
                            }}
                            renderInput={(params) => <TextField {...params} label="Circolo PD *" variant="outlined" />}
                          />
                        </div>
                      ) : (
                        <CustomAutocomplete
                          required
                          disabled={disableEntity}
                          id="register-entity"
                          options={EntitiesList}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          value={entity}
                          onChange={(e, value) => {
                            setEntity(value);
                          }}
                          renderInput={(params) => (
                            <TextField error={errors.entity_id} {...params} label="Circolo PD *" variant="outlined" />
                          )}
                        />
                      )}
                    </Grid>
                    {isGdMode && (
                      <Grid item xs={12} lg={10} sx={{ order: 1, mt: -1.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Devi definire un Circolo PD di riferimento, questa selezione non comporta l&apos;iscrizione al
                          Partito Democratico.
                        </Typography>
                      </Grid>
                    )}
                    {isBlacklistedEntity && (
                      <Grid item xs={12} lg={10} sx={{ order: 1 }}>
                        <Alert severity="error">
                          Attenzione! Al momento il tesseramento online in questo territorio è sospeso, puoi comunque
                          completare la registrazione al portale. Verrai avvisato nel momento in cui sarà possibile
                          iscriversi anche sul tuo territorio.
                        </Alert>
                      </Grid>
                    )}

                    {emptyEntitiesList && (
                      <Grid item xs={12} lg={10} sx={{ order: 1 }}>
                        <Alert severity="error">
                          Attenzione! Nessuna entità disponibile per la registrazione, contattare l'helpdesk
                          all'indirizzo tesseramento@partitodemocratico.it per ulteriori informazioni.
                        </Alert>
                      </Grid>
                    )}

                    {/* Entità master GD (entity_master_gd) — GD-only e PD-bundle (sprint 019) */}
                    {/* In /join-gd l'Entità GD precede il Circolo (order 0); nel bundle PD resta dopo (order 3) */}
                    {(isGdMode || gdCheck) && (
                      <Grid item xs={12} md={12} lg={10} sx={{ order: isGdMode ? 0 : 3 }}>
                        <GdMasterEntitySelect
                          value={gdEntity}
                          onChange={setGdEntity}
                          options={gdEntityList}
                          error={errors.entity_master_gd}
                          disabled={gdEntityListEmpty}
                          label="Entità di iscrizione GD *"
                        />
                        {gdEntityListEmpty && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            Nessuna entità disponibile per la registrazione ai Giovani Democratici
                          </Alert>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Box>

                <Stack direction="row" sx={{ padding: '32px 0' }}>
                  <Grid justifyContent="center" alignItems="center" container spacing={2}>
                    {!isSubscriptionLocked && hasLessThan16AndMoreThan16YO && (
                      <Grid item xs={12} lg={10}>
                        <Alert severity="warning">
                          Sotto i 16 anni puoi iscriverti solo ai Giovani Democratici.{' '}
                          <Link component={RouterLink} to="/join-gd">
                            Vai alla pagina di iscrizione
                          </Link>
                          .
                        </Alert>
                      </Grid>
                    )}
                    {!registerLoginDone && (
                      <>
                        {isSubscriptionLocked && (
                          <Grid item xs={12} lg={10}>
                            <Alert severity="warning">
                              Al momento non ci sono campagne disponibili per il tesseramento.
                            </Alert>
                          </Grid>
                        )}

                        <Grid
                          item
                          xs={12}
                          lg={10}
                          sx={{ display: isVolounteerRegistration || isGdMode ? 'none' : 'flex' }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={cardCheck}
                                disabled={isBlacklistedEntity || isSubscriptionLocked}
                                onChange={handleCardCheck}
                              />
                            }
                            label={
                              activeCampaign && Object.keys(activeCampaign).length > 0
                                ? `Tesseramento ${activeCampaign.description}`
                                : `Tesseramento non disponibile`
                            }
                            labelPlacement="end"
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          lg={10}
                          sx={{
                            display: isVolounteerRegistration || !registrationConfig.showGdCheckbox ? 'none' : 'flex',
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                disabled={!cardCheck || !isGdEligible}
                                checked={gdCheck}
                                onChange={handleGdCheck}
                              />
                            }
                            label="Voglio essere iscritto ai Giovani Democratici"
                            labelPlacement="end"
                          />
                        </Grid>
                      </>
                    )}

                    {/* Position section Check */}
                    <Grid item xs={12} lg={10} sx={{ display: isVolounteerRegistration ? 'none' : 'flex' }}>
                      {registerLoginDone > 0 ? (
                        <FormControlLabel
                          control={<Checkbox disabled checked={loginHasRole} />}
                          label="Appartengo alla categoria degli amministratori locali o dei componenti delle assemblee legislative e degli esecutivi a livello regionale e nazionale"
                        />
                      ) : (
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={!cardCheck}
                              checked={hasRole}
                              onChange={(e) => {
                                setHasRole(e.target.checked);
                              }}
                            />
                          }
                          label="Appartengo alla categoria degli amministratori locali o dei componenti delle assemblee legislative e degli esecutivi a livello regionale e nazionale"
                        />
                      )}
                    </Grid>

                    {/* Select Position Dynamic Section */}
                    {(hasRole || loginHasRole) && cardCheck && (
                      <Grid style={{ opacity: roleDynamicContainer, transition: 'opacity 1s' }} item xs={12} lg={10}>
                        <Box sx={{ borderRadius: '8px', padding: '5px 20px' }}>
                          {/* , paddingTop: '8px' */}
                          <Typography sx={{ fontSize: '.8em' }}>
                            Se rientri in una delle suddette categorie, specifica quale
                          </Typography>

                          <Box sx={{ paddingTop: '6px' }}>
                            {registerLoginDone > 0 ? (
                              <CustomTextField disabled value={loginPosition.name} />
                            ) : (
                              <CustomAutocomplete
                                size="small"
                                options={PositionsList}
                                getOptionLabel={(option) => option.name}
                                renderInput={(params) => <TextField {...params} label="Carica" variant="outlined" />}
                                onChange={(e, value) => {
                                  setRole(value);
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {!registerLoginDone && (
                      <Grid item xs={12} lg={10}>
                        <Stack direction="column" gap="12px">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={privacyChecked}
                                onChange={(e) => {
                                  setPrivacyChecked(e.target.checked);
                                }}
                              />
                            }
                            label={
                              <>
                                Con la sottoscrizione del presente modulo dichiaro di aver letto e compreso l'
                                <Link href="https://partitodemocratico.it/privacy-policy/" target="_blank">
                                  informativa privacy
                                </Link>{' '}
                                e di autorizzare il trattamento dei miei dati particolari ai sensi dell'art. 9, c. 2,
                                del Reg.(UE)2016/679
                              </>
                            }
                          />

                          <FormControlLabel
                            control={<Checkbox checked={newsletterChecked} onChange={handleNewsletterCheck} />}
                            label={
                              isGdMode
                                ? 'Acconsento ad essere successivamente ricontattato dai Giovani Democratici e dal Partito Democratico con comunicazioni di propaganda politica ed elettorale in linea con le finalità statutarie, anche a mezzo sms, e-mail, messaggistica istantanea e social network'
                                : 'Acconsento ad essere successivamente ricontattato dal Partito Democratico con comunicazioni di propaganda politica ed elettorale in linea con le finalità statutarie, anche a mezzo sms, e-mail, messaggistica istantanea e social network'
                            }
                            labelPlacement="end"
                          />
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </Stack>

                {/* Registration user request */}
                <Stack direction="row" alignItems="center" justifyContent="center" id="register-submit-button">
                  {registerLoginDone ? (
                    <DefaultContainedButton
                      customStyles={{ width: is1200Screen ? '75%' : '100%', height: '40px', marginTop: '24px' }}
                      type="submit"
                    >
                      Successivo
                    </DefaultContainedButton>
                  ) : (
                    <DefaultContainedButton
                      customStyles={{ width: is1200Screen ? '75%' : '100%', height: '40px' }}
                      type="submit"
                      disabled={!privacyChecked || emptyEntitiesList}
                    >
                      Successivo
                    </DefaultContainedButton>
                  )}
                </Stack>
              </form>
            </Box>
          )}

          {/* Registration Step 2 | verify contacts */}
          {registrationStep === 2 && isNewRegistration && (
            <Stack sx={{ height: is600Screen ? 'auto' : 'calc(100vh - 48px)' }}>
              {/* Stepper */}
              <Box sx={{ width: '100%', marginBottom: '32px' }}>
                <Stepper activeStep={registrationStep - 1} alternativeLabel>
                  <Step key={1}>
                    <StepLabel>Anagrafica</StepLabel>
                  </Step>
                  <Step key={2}>
                    <StepLabel>Verifica</StepLabel>
                  </Step>
                  <Step key={3}>
                    <StepLabel>Password</StepLabel>
                  </Step>
                  {cardCheck && (
                    <Step key={4}>
                      <StepLabel>Tesseramento</StepLabel>
                    </Step>
                  )}
                </Stepper>
              </Box>

              {/* Contacts Verification */}
              <Box sx={{ marginTop: '50px', flexGrow: '1' }}>
                <Grid container justifyContent="center" alignItems="start" spacing={2} gap={2} sx={{ height: '100%' }}>
                  {/* Verify Code Text */}
                  <Grid item xs={12} md={7} sx={{ textAlign: 'center' }}>
                    <Typography fontSize={32} fontWeight={600}>
                      Verifica Contatti
                    </Typography>

                    <Typography sx={{ maxWidth: '400px', margin: '0 auto' }}>
                      Abbiamo inviato un codice di verifica all'indirizzo email e al numero di telefono utilizzati
                      durante la prima fase di registrazione.
                    </Typography>

                    <Typography
                      variant="span"
                      sx={{ fontWeight: 'bold', textDecoration: 'underline', display: 'inline' }}
                    >
                      Per proseguire è necessario verificare entrambi i contatti
                    </Typography>
                  </Grid>

                  {/* Verify Code Input */}
                  <Grid item xs={12} md={12}>
                    <Stack
                      direction="column"
                      justifyContent="space-evenly"
                      alignItems="center"
                      gap={is600Screen ? '30px' : '60px'}
                    >
                      {/* Email */}
                      <Stack
                        justifyContent="center"
                        alignItems="center"
                        gap="12px"
                        id="register-email-token-container"
                        sx={{ width: '100%' }}
                      >
                        <Stack
                          direction="column"
                          justifyContent="center"
                          alignItems="center"
                          gap="12px"
                          sx={{ width: '100%' }}
                        >
                          {/* Email Address */}
                          <Stack
                            direction={is600Screen ? 'row' : 'column'}
                            justifyContent="center"
                            alignItems="center"
                            gap="6px"
                            sx={{ width: '100%' }}
                          >
                            {/* <Typography variant="h6">Email:</Typography>
                            <Typography variant="span">{emailAddress}</Typography> */}

                            <CustomTextField
                              label="Email"
                              value={newEmailAddress}
                              customStyles={{ maxWidth: '300px' }}
                              disabled={disableEmailAddress}
                              onChange={(e) => setNewEmailAddress(e.currentTarget.value)}
                            />

                            {verifiedEmail ? (
                              <Tooltip title="Email verificata" placement="right">
                                <CheckIcon sx={{ color: 'green' }} />
                              </Tooltip>
                            ) : isUserVerifyingEmail ? (
                              <Tooltip title="In attesa di verifica" placement="right">
                                <AutorenewIcon sx={{ color: '#414670' }} />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Email non verificata" placement="right">
                                <WarningAmberIcon sx={{ color: theme.palette.orange.main }} />
                              </Tooltip>
                            )}

                            {!verifiedEmail && (
                              <Tooltip arrow title="Modifica Indirizzo" placement="top">
                                <Box>
                                  <DefaultOutlinedButton onClick={handleSubmitChangeEmail} type="button">
                                    {/* <EditIcon/> */}
                                    {disableEmailAddress ? 'Modifica Email' : 'Salva Indirizzo'}
                                  </DefaultOutlinedButton>
                                </Box>
                              </Tooltip>
                            )}
                          </Stack>

                          {isUserVerifyingEmail ? (
                            <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                              {/* Input & Submit Button */}
                              <Stack direction="row" justifyContent="center" alignItems="center" gap="12px">
                                <CustomTextField
                                  size="small"
                                  inputProps={{ maxLength: 6 }}
                                  onChange={(e) => setEmailVerificationCode(e.currentTarget.value)}
                                  label="Inserisci il codice"
                                  disabled={!disableEmailAddress}
                                />
                                <DefaultOutlinedButton
                                  disabled={verifiedEmail || !disableEmailAddress}
                                  onClick={() => onVerifyEmailOTP()}
                                >
                                  Verifica
                                </DefaultOutlinedButton>
                              </Stack>
                            </Stack>
                          ) : (
                            !verifiedEmail && (
                              <>
                                <DefaultOutlinedButton onClick={() => setIsUserVerifyingEmail(true)}>
                                  Verifica Email
                                </DefaultOutlinedButton>
                              </>
                            )
                          )}

                          {/* Resend Email */}
                          {!verifiedEmail && (
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography>Non hai ricevuto l'email?</Typography>

                              <Typography
                                onClick={() => {
                                  if (disableEmailAddress) {
                                    resendVerificationEmail();
                                  }
                                }}
                                sx={{
                                  textDecoration: 'underline',
                                  color: emailCountdown || !disableEmailAddress > 0 ? 'gray' : 'blue',
                                  cursor:
                                    emailCountdown > 0 ? 'not-allowed' : !disableEmailAddress ? 'inherit' : 'pointer',
                                }}
                              >
                                {emailCountdown > 0 ? `Riprova tra ${emailCountdown}s` : 'Inviala di nuovo'}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Stack>

                      {/* Phone */}
                      <Stack justifyContent="center" alignItems="center" gap="12px" id="register-email-token-container">
                        <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                          {/* Phone Number */}
                          <Stack
                            direction={is600Screen ? 'row' : 'column'}
                            justifyContent="center"
                            alignItems="center"
                            gap="6px"
                          >
                            <Typography variant="h6">Numero di telefono:</Typography>

                            <Typography variant="span">{phoneNumber}</Typography>
                            {verifiedPhone ? (
                              <Tooltip title="Numero verificato" placement="right">
                                <CheckIcon sx={{ color: 'green' }} />
                              </Tooltip>
                            ) : isUserVerifyingPhone ? (
                              <Tooltip title="In attesa di verifica" placement="right">
                                <AutorenewIcon sx={{ color: '#414670' }} />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Numero non verificato" placement="right">
                                <WarningAmberIcon sx={{ color: theme.palette.orange.main }} />
                              </Tooltip>
                            )}
                          </Stack>
                          {isUserVerifyingPhone ? (
                            <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                              <Stack direction="row" justifyContent="center" alignItems="center" gap="12px">
                                <CustomTextField
                                  size="small"
                                  inputProps={{ maxLength: 6 }}
                                  onChange={(e) => setPhoneVerificationCode(e.currentTarget.value)}
                                  label="Inserisci il codice"
                                />
                                <DefaultOutlinedButton disabled={verifiedPhone} onClick={() => onVerifySmsOTP()}>
                                  Verifica
                                </DefaultOutlinedButton>
                              </Stack>
                            </Stack>
                          ) : (
                            !verifiedPhone && (
                              <DefaultOutlinedButton onClick={() => setIsUserVerifyingPhone(true)}>
                                Verifica numero
                              </DefaultOutlinedButton>
                            )
                          )}
                        </Stack>

                        {/* Resend SMS */}
                        {!verifiedPhone && (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography>Non hai ricevuto l'sms?</Typography>

                            <Typography
                              onClick={() => resendVerificationSms()}
                              sx={{
                                textDecoration: 'underline',
                                color: smsCountdown > 0 ? 'gray' : 'blue',
                                cursor: smsCountdown > 0 ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {smsCountdown > 0 ? `Riprova tra ${smsCountdown}s` : 'Invialo di nuovo'}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sx={{ flexGrow: '2', alignSelf: 'end' }}>
                    <Stack direction="row" justifyContent="center" alignItems="center">
                      {/* proceed to checkout button */}
                      <DefaultContainedButton
                        customStyles={{
                          textTransform: 'none',
                          height: '40px',
                          justifySelf: 'end',
                          margin: '20px 0',
                          padding: '0 30px',
                        }}
                        disabled={!verifiedEmail && !verifiedPhone}
                        onClick={() => onCompletedVerification()}
                      >
                        Procedi
                      </DefaultContainedButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          )}

          {/* Registration Step 3 | password */}
          {registrationStep === 3 && isNewRegistration && (
            <Box sx={{ height: is600Screen ? 'auto' : '95vh' }}>
              {/* Stepper */}
              <Box sx={{ width: '100%', marginBottom: '32px' }}>
                <Stepper activeStep={registrationStep - 1} alternativeLabel>
                  <Step key={1}>
                    <StepLabel>Anagrafica</StepLabel>
                  </Step>
                  <Step key={2}>
                    <StepLabel>Verifica</StepLabel>
                  </Step>
                  <Step key={3}>
                    <StepLabel>Password</StepLabel>
                  </Step>
                  {cardCheck && (
                    <Step key={4}>
                      <StepLabel>Tesseramento</StepLabel>
                    </Step>
                  )}
                </Stepper>
              </Box>

              <Box sx={{ paddingTop: '32px', height: '90%' }}>
                <form method="post" onSubmit={handleSetUserPassword} style={{ height: '100%' }}>
                  <Grid
                    container
                    justifyContent="center"
                    alignContent="start"
                    spacing={2}
                    gap={2}
                    sx={{ height: '100%' }}
                  >
                    <Grid item xs={12} md={8}>
                      <Typography variant="h4" sx={{ textAlign: 'center' }}>
                        Definisci la tua password
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Alert variant="filled" severity="warning">
                        Potrai entrare nel tuo profilo utente del PD HUB. Ricorda che il tuo profilo è personale, non
                        puoi condividerlo con altri.
                      </Alert>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Typography>La password deve contenere almeno:</Typography>
                      <Typography>&#x2022; 8 caratteri</Typography>
                      <Typography>&#x2022; Una lettera maiuscola</Typography>
                      <Typography>&#x2022; Un numero</Typography>
                      <Typography>&#x2022; Uno dei seguenti caratteri speciali !?-@#$%^&*()_+</Typography>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <CustomFormControl>
                        <InputLabel htmlFor="new-password">Password</InputLabel>
                        <OutlinedInput
                          id="new-password"
                          type={showNewPassword ? 'password' : 'text'}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowNewPassword}
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseUp={(e) => e.preventDefault()}
                                edge="end"
                              >
                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          }
                          label="Password"
                          name="newPassword"
                        />
                      </CustomFormControl>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <CustomFormControl>
                        <InputLabel htmlFor="repeat-password">Ripeti Password</InputLabel>
                        <OutlinedInput
                          id="repeat-password"
                          type={showRepeatPassword ? 'password' : 'text'}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowRepeatPassword}
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseUp={(e) => e.preventDefault()}
                                edge="end"
                              >
                                {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          }
                          label="Ripeti Password"
                          name="repeatPassword"
                        />
                      </CustomFormControl>
                    </Grid>

                    <Grid item xs={12} md={8} sx={{ alignSelf: 'end', flexGrow: '2' }}>
                      <Stack direction="row" justifyContent="center" sx={{ height: '100%' }}>
                        <DefaultContainedButton
                          customStyles={{
                            width: '100%',
                            height: '40px',
                            marginTop: '10px',
                            marginBottom: '10px',
                            alignSelf: 'end',
                          }}
                          type="submit"
                          disabled={isValidatedPassword}
                        >
                          {cardCheck ? 'Procedi al tesseramento' : 'Imposta password e completa la registrazione'}
                        </DefaultContainedButton>
                      </Stack>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            </Box>
          )}

          {/* Registration Step 4 | subscription */}
          {registrationStep === 4 && (isNewRegistration || isRenewRegistration) && cardCheck && (
            <Box>
              <Box sx={{ width: '100%', marginBottom: '32px' }}>
                {/* Stepper */}
                <Stepper activeStep={registrationStep - 1} alternativeLabel>
                  <Step key={1}>
                    <StepLabel>Anagrafica</StepLabel>
                  </Step>
                  <Step key={2}>
                    <StepLabel>Verifica</StepLabel>
                  </Step>
                  <Step key={3}>
                    <StepLabel>Password</StepLabel>
                  </Step>
                  <Step key={4}>
                    <StepLabel>Tesseramento</StepLabel>
                  </Step>
                </Stepper>
              </Box>

              {/* Payment Review */}
              <Box>
                <Grid container justifyContent="center" alignItems="center" spacing={2}>
                  {/* Rates list */}
                  <Grid item xs={12} md={10} sx={{ overflow: 'auto', maxWidth: 'unset' }}>
                    {membershipCard.id === null ? (
                      <>
                        <RatesTable
                          rates={activeRates}
                          disabledStates={disabledStates}
                          otherValue={otherValue}
                          setOtherValue={setOtherValue}
                          onRateChange={handleRateChange}
                          onToggleEditable={handleDisabledInput}
                          getReadonlyPrice={(rate) => (!isGdMode && isGdAvailable ? rate.price_discounted : rate.price)}
                          totalPrice={totalPrice}
                          showCurrencyColumn={is600Screen}
                        />
                      </>
                    ) : (
                      <>
                        <Card
                          sx={{ maxWidth: 'xl', padding: '24px', backgroundColor: 'snow' }}
                          variant="outlined"
                          key={membershipCard.id}
                        >
                          <Grid
                            container
                            spacing={is600Screen ? 3 : 0}
                            justifyContent={is600Screen ? 'space-evenly' : 'space-around'}
                          >
                            <Grid item xs={12} sm={6}>
                              {/* Membership card content */}
                              <CardContent sx={{ flex: '1 0 auto', padding: '0 !important' }}>
                                {/* campaign name */}
                                <Typography component="div" variant="h6">
                                  Tessera n°
                                  {membershipCard.card_code || `${membershipCard.prefix}${membershipCard.card_number}`}
                                </Typography>
                              </CardContent>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <CardContent sx={{ flex: '1 0 auto', padding: '0 !important' }}>
                                {/* Entity name */}
                                <Typography variant="subtitle1" color="text.secondary" component="div">
                                  Circolo: {membershipCard.entity_name}
                                </Typography>

                                {/* Membership Card registration date */}
                                <Typography variant="subtitle1" color="text.secondary" component="div">
                                  Data: {dayjs(membershipCard.creation_date).format('DD/MM/YYYY')}
                                </Typography>

                                {/* Total price */}
                                <Typography variant="subtitle1" color="text.secondary" component="div">
                                  Importo: {totalPrice.toFixed(2)} {membershipCard.currency}
                                </Typography>
                              </CardContent>
                            </Grid>
                          </Grid>
                        </Card>

                        <Grid item xs={12}>
                          <Alert variant="filled" severity="warning" sx={{ marginTop: 2 }}>
                            Se non completi il pagamento, ricorda che il tuo account è stato creato e ti basterà
                            accedere per effettuare il tesseramento senza dover inserire nuovamente i dati.
                          </Alert>
                        </Grid>
                      </>
                    )}
                  </Grid>
                  {/* qui */}

                  {!is600Screen && membershipCard.id !== null && (
                    <>
                      <PaymentSection mobile />
                    </>
                  )}

                  {membershipCard.id === null && (
                    <Grid item xs={12} md={10}>
                      <Alert variant="filled" severity="warning" sx={{ marginTop: 2 }}>
                        Se non completi il pagamento, ricorda che il tuo account è stato creato e ti basterà accedere
                        per effettuare il tesseramento senza dover inserire nuovamente i dati.
                      </Alert>
                    </Grid>
                  )}

                  {/* checkout section footer buttons */}
                  <Grid item container xs={12} md={10}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        {membershipCard.id === null && (
                          <FormControlLabel
                            sx={{ paddingTop: '16px' }}
                            control={
                              <Switch
                                checked={onlineMembershipCard}
                                onChange={() => setOnlineMembershipCard(!onlineMembershipCard)}
                              />
                            }
                            label={onlineMembershipCard ? 'Tessera Online' : 'Tessera Cartacea'}
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <DefaultOutlinedButton
                          customStyles={{
                            marginTop: '20px',
                            marginRight: '12px',
                            display: membershipCard.id === null ? 'block' : 'none',
                          }}
                          onClick={skipCardCreation}
                        >
                          Chiudi
                        </DefaultOutlinedButton>
                        <DefaultContainedButton
                          customStyles={{
                            marginTop: '20px',
                            display: membershipCard.id === null ? 'block' : 'none',
                          }}
                          disabled={!isSaveEnabled}
                          onClick={handleOpenCreateCardDialog}
                        >
                          Paga ora
                        </DefaultContainedButton>
                      </Grid>
                    </Grid>

                    <Dialog open={openCreateCardDialog} onClose={handleCloseCreateCardDialog}>
                      <DialogTitle>Confermi?</DialogTitle>
                      <DialogContent>
                        <Typography>
                          Proseguendo la tariffa libera non sarà più modificabile e il contributo del tesseramento sarà
                          di:{` ${totalPrice.toFixed(2)} €`}
                        </Typography>
                      </DialogContent>
                      <DialogActions>
                        <DefaultOutlinedButton onClick={handleCloseCreateCardDialog}>Chiudi</DefaultOutlinedButton>
                        <DefaultContainedButton onClick={onCreateMemCard}>Continua</DefaultContainedButton>
                      </DialogActions>
                    </Dialog>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}

          {/* Registration success message section */}
          {registrationStep === 5 && (
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
              gap="25px"
              sx={{ padding: '120px 0', textAlign: 'center' }}
            >
              {step5Data.icon === 'warning' ? (
                <WarningAmberIcon sx={{ fontSize: '5em', color: theme.palette.orange.main }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: '5em', color: 'green' }} />
              )}

              <Typography variant="h3">{step5Data.text}</Typography>
              <DefaultContainedButton
                href="/"
                customStyles={{
                  marginTop: '36px',
                }}
              >
                Vai alla pagina di login
              </DefaultContainedButton>
            </Stack>
          )}
        </Box>

        {is600Screen && registrationStep === 4 && (isNewRegistration || isRenewRegistration) && (
          <Box
            sx={{
              padding: is1200Screen ? '24px 24px' : '24px 18px',
              borderRadius: is600Screen ? '8px' : '0',
              backgroundColor: 'white',
              boxShadow: is600Screen && '0px 0px 21px 3px rgba(0, 0, 0, 0.5)',
              display: createdCard ? 'block' : 'none',
              overflow: 'auto',
              marginBottom: is600Screen && '25px',
            }}
            width={is1200Screen ? '950px' : is900Screen ? '700px' : is600Screen ? '580px' : '100%'}
          >
            <PaymentSection />
          </Box>
        )}
      </Stack>
    </>
  );
}
